/* eslint-disable */
/* gridRotations no long in use for plots and replicants */

import * as turf from "@turf/turf";
import { getSubPolygons } from "../../../shared/utils/geoUtils";
import mapboxgl from "mapbox-gl";
import moveIcon from "../../../assets/images/moveIcon.svg";
import rotateImage from "../../../assets/images/rotation.svg";
import {
  autozoneRequestStatus,
  ProductivityZonesInterface,
} from "../../../pages/autozones/types";
import { useNewTrialState } from "../../../pages/new-trail/hooks/NewTrialStateProvider";
import {
  type AbLine,
  type NewTrialStateInterface,
  type PlotsValidationData,
  type RatesDosageDataInterface,
  type Replicants,
  type TrialPlot,
} from "../../../pages/new-trail/types";
import { allowFieldInputChange } from "../../../pages/new-trail/utils";
import {
  updateTrialPlotsWithStripGeometries,
  usePlotCardState,
} from "../../../shared/layout/PlotCard/utils";
import {
  getCircleColor,
  getCircleColorBiologicals,
  getCircleColorNutrient,
} from "../../../shared/layout/Plots/utils";
import { drawMachineryGridPure } from "../../utils/abLine";
import { addAutoZonesLayer } from "../../utils/autozones";
import { addBufferZoneLayer } from "../../utils/bufferZone";
import { addFieldOperationsLayer } from "../../utils/fieldOperations";
import { addABLinesToMap } from "../../utils/line";
import { addSoilImageLayer, removeSoilImageLayer } from "../../utils/soilData";
import {
  addStripLinesToMap,
  removeStripLinesFromMap,
} from "../../utils/stripLineJitendra";
import {
  calculateStripAreas,
  generateParallelLines,
} from "../../utils/parallelLinesLayer";
import {
  type FieldDataInterface,
  type GridFeature,
  type GridPosition,
  type PolygonGeoJSON,
  type TrialInfoInterface,
} from "./type";

const FEATURES_INDEX_ZERO = 0;
const randomPointsCache: Record<
  number,
  turf.FeatureCollection<turf.Point>
> = {};
let previousTrialPlots: TrialPlot[] | null = null;
const RGBA_TRIAL_PLOT = "white";
const RGBA_TRIAL_PLOT_RED = "rgba(255,0,0,1)";
export const markersForTrialPlot: Record<string, mapboxgl.Marker> = {};
const INDEX_ZERO = 0;
const INDEX_ONE = 1;
const RGBA_DASH_LINE = "rgba(49, 180, 242, 1)";
const WHITE_COLOR = "white";
type GeoJSON = turf.FeatureCollection<turf.Geometry>;
const gridPositions: Record<number, GridPosition> = {};
const gridRotations: Record<number, number> = {};
const RGBA_TRANSPARENT_COLOR = "rgba(0,0,0,0)";
const RGBA_PLOT_FILL = "rgba(255, 255, 255, 0.10)";

// --- AB line alignment tracking ---
type AlignmentChangeCallback = (misaligned: boolean) => void;
let alignmentChangeCallback: AlignmentChangeCallback | null = null;
let moduleMap: mapboxgl.Map | null = null;
/** Live reference to the latest newTrialState – mutated in-place by drag handlers */
let moduleNewTrialState: NewTrialStateInterface | null = null;

/**
 * Reads the index of the AB line marked `selectedForAlignment` from state.
 * Falls back to 0 so the first AB line is always the default target.
 */
const getAlignmentAbLineIndex = (state: NewTrialStateInterface): number => {
  const idx = state.ab_line?.findIndex((ab: AbLine) => ab.selectedForAlignment);
  return idx !== undefined && idx >= 0 ? idx : 0;
};

/** Register a callback that fires whenever plot alignment with the AB line changes. */
export const registerAlignmentChangeCallback = (
  cb: AlignmentChangeCallback | null
): void => {
  alignmentChangeCallback = cb;
};

/** @deprecated Selection is now driven by `selectedForAlignment` on state – this is a no-op kept for compile compatibility. */
export const setAlignmentAbLineIndex = (_idx: number): void => {};

/**
 * Helper: minimum angular difference (0–90) between two *undirected* bearings.
 * Treats a bearing and its 180° reverse as identical (same line, opposite direction)
 * so an AB line stored in either coordinate order is handled correctly.
 */
const bearingDiff = (a: number, b: number): number => {
  const diff = Math.abs(((a - b + 540) % 360) - 180);
  return Math.min(diff, 180 - diff);
};

const ALIGNMENT_TOLERANCE_DEG = 2;
// Distance a plot border may sit from a machinery guide line and still count as
// "aligned". The machinery grid offsets are re-measured from the rendered map
// source, whose geometry is tile-quantised (sub-metre) and follows a sampled
// rhumb path (documented residual up to ~0.21 m at 20 km from the A/B origin).
// A 0.25 m tolerance left almost no headroom, so a plot the user had just
// snapped onto a line was intermittently re-flagged as misaligned. 1 m is still
// far tighter than half a grid spacing (typically ≥ 10 m), so genuinely
// off-grid plots are still detected.
const COLLINEAR_DISTANCE_TOLERANCE_METERS = 1.0;

export const getSourceLineFeatures = (
  map: mapboxgl.Map,
  sourceId: string
): turf.Feature<turf.LineString>[] => {
  const toLineFeatures = (features: any[]): turf.Feature<turf.LineString>[] =>
    features.filter(
      (feature): feature is turf.Feature<turf.LineString> =>
        feature?.geometry?.type === "LineString"
    );

  // 1) Preferred: query currently available source features through Map API.
  try {
    const queried = map.querySourceFeatures(sourceId as any) as any[];
    if (Array.isArray(queried) && queried.length > 0) {
      const lines = toLineFeatures(queried);
      if (lines.length > 0) return lines;
    }
  } catch {
    // Source may not be queryable yet (not loaded / missing tiles) – continue fallback chain.
  }

  const source = map.getSource(sourceId) as mapboxgl.GeoJSONSource | undefined;
  const sourceAny = source as any;

  // 2) Semi-public: source.getData() (available in some mapbox/maplibre builds).
  try {
    const maybeData = sourceAny?.getData?.();
    if (maybeData && typeof maybeData.then !== "function") {
      const maybeFeatures = Array.isArray(maybeData?.features)
        ? maybeData.features
        : [];
      const lines = toLineFeatures(maybeFeatures);
      if (lines.length > 0) return lines;
    }
  } catch {
    // Keep falling back.
  }

  // 3) Style-source definition often carries inline GeoJSON `data` for source type geojson.
  const styleSourceData = (map.getStyle()?.sources as any)?.[sourceId]?.data;
  if (styleSourceData && Array.isArray(styleSourceData?.features)) {
    const lines = toLineFeatures(styleSourceData.features);
    if (lines.length > 0) return lines;
  }

  // 4) Last resort: private field used historically by GeoJSONSource.
  const privateData = sourceAny?._data;
  const privateFeatures = Array.isArray(privateData?.features)
    ? privateData.features
    : [];
  return toLineFeatures(privateFeatures);
};

const getAlignmentGuideLines = (
  state: NewTrialStateInterface,
  map: mapboxgl.Map
): turf.Feature<turf.LineString>[] => {
  const alignmentAb = state.ab_line?.[getAlignmentAbLineIndex(state)];
  const abCoords = alignmentAb?.geojson?.geometry?.coordinates;
  if (!abCoords || abCoords.length < 2) return [];

  const abLineFeature = turf.lineString([
    abCoords[0] as [number, number],
    abCoords[1] as [number, number],
  ]);

  const gridSourceId = `grid-layer-${alignmentAb?.id ?? ""}`;
  const regularGridLines = getSourceLineFeatures(map, gridSourceId);
  const middleGridLines = getSourceLineFeatures(map, `${gridSourceId}-gray`);

  return [abLineFeature, ...regularGridLines, ...middleGridLines];
};

const getSignedPerpendicularDistanceKm = (
  originCoord: [number, number],
  pointCoord: [number, number],
  perpendicularBearing: number
): number => {
  const origin = turf.point(originCoord);
  const point = turf.point(pointCoord);
  const distanceKm = turf.rhumbDistance(origin, point, { units: "kilometers" });
  const bearingToPoint = turf.rhumbBearing(origin, point);
  const angleDiff = ((bearingToPoint - perpendicularBearing + 540) % 360) - 180;
  return distanceKm * Math.cos((angleDiff * Math.PI) / 180);
};

const getPlotBorderDistanceBandKm = (
  featureCollection: turf.FeatureCollection<turf.Polygon>
): { min: number; max: number; points: [number, number][] } => {
  const points: [number, number][] = [];
  featureCollection.features.forEach((feature) => {
    const ring = feature?.geometry?.coordinates?.[0] ?? [];
    ring.forEach((coord) => {
      if (Array.isArray(coord) && coord.length >= 2) {
        points.push([coord[0], coord[1]]);
      }
    });
  });

  if (points.length === 0) {
    return {
      min: Number.POSITIVE_INFINITY,
      max: Number.NEGATIVE_INFINITY,
      points,
    };
  }

  const lngValues = points.map((point) => point[0]);
  return {
    min: Math.min(...lngValues),
    max: Math.max(...lngValues),
    points,
  };
};

const getGuideLineOffsetsKm = (
  guideLines: turf.Feature<turf.LineString>[],
  originCoord: [number, number],
  perpendicularBearing: number
): number[] => {
  const offsets = guideLines
    .map((line) => {
      const coords = line?.geometry?.coordinates;
      if (!coords || coords.length === 0) return null;
      const sampleCoord = coords[Math.floor(coords.length / 2)] as [
        number,
        number
      ];
      return getSignedPerpendicularDistanceKm(
        originCoord,
        sampleCoord,
        perpendicularBearing
      );
    })
    .filter(
      (offset): offset is number =>
        typeof offset === "number" && isFinite(offset)
    );

  return Array.from(new Set(offsets.map((value) => Number(value.toFixed(9)))));
};

const snapFeatureCollectionToGuides = (
  featureCollection: turf.FeatureCollection<turf.Polygon>,
  pivot: [number, number],
  abCoords: [number, number][],
  abBearing: number,
  perpendicularBearing: number,
  guideOffsetsKm: number[]
): turf.FeatureCollection<turf.Polygon> => {
  const firstRing = featureCollection.features[0]?.geometry?.coordinates?.[0];
  if (!firstRing || firstRing.length < 2) {
    return featureCollection;
  }

  const currentEdgeBearing = turf.bearing(
    turf.point(firstRing[0]),
    turf.point(firstRing[1])
  );

  const normalizeDelta = (deltaValue: number): number =>
    ((deltaValue + 540) % 360) - 180;
  const delta1 = normalizeDelta(abBearing - currentEdgeBearing);
  const delta2 = normalizeDelta(abBearing + 180 - currentEdgeBearing);
  const delta = Math.abs(delta1) <= Math.abs(delta2) ? delta1 : delta2;

  const snappedFC = turf.transformRotate(featureCollection as any, delta, {
    pivot,
  }) as turf.FeatureCollection<turf.Polygon>;

  if (guideOffsetsKm.length === 0) {
    return snappedFC;
  }

  const band = getPlotBorderDistanceBandKm(snappedFC);
  if (!isFinite(band.min) || !isFinite(band.max)) {
    return snappedFC;
  }

  let minOffset = Number.POSITIVE_INFINITY;
  let maxOffset = Number.NEGATIVE_INFINITY;

  band.points.forEach((coord) => {
    const offset = getSignedPerpendicularDistanceKm(
      abCoords[0] as [number, number],
      coord,
      perpendicularBearing
    );
    if (offset < minOffset) minOffset = offset;
    if (offset > maxOffset) maxOffset = offset;
  });

  const borderOffsets = [minOffset, maxOffset];
  let bestShiftKm = 0;
  let bestShiftAbs = Number.POSITIVE_INFINITY;

  borderOffsets.forEach((borderOffset) => {
    guideOffsetsKm.forEach((guideOffset) => {
      const shiftKm = guideOffset - borderOffset;
      const absShift = Math.abs(shiftKm);
      if (absShift < bestShiftAbs) {
        bestShiftAbs = absShift;
        bestShiftKm = shiftKm;
      }
    });
  });

  if (!isFinite(bestShiftKm) || Math.abs(bestShiftKm) <= 1e-9) {
    return snappedFC;
  }

  return turf.transformTranslate(
    snappedFC as any,
    bestShiftKm,
    perpendicularBearing,
    {
      units: "kilometers",
    }
  ) as turf.FeatureCollection<turf.Polygon>;
};

interface SnapRenderablePlotOptions {
  sourceId: string;
  centerMarkerId: string;
  rotationMarkerId: string;
  rawCoords?: any[];
  setCoordinates: (coordinates: any[]) => void;
  onAfterSnap?: (translatedFC: turf.FeatureCollection<turf.Polygon>) => void;
}

const validateSnapTargetInputs = (
  target: SnapRenderablePlotOptions,
  source: mapboxgl.GeoJSONSource | undefined
): boolean => {
  const centerMarker = markersForTrialPlot[target.centerMarkerId];
  return (
    !!source &&
    !!centerMarker &&
    !!target.rawCoords &&
    target.rawCoords.length > 0
  );
};

const buildFeatureCollectionFromCoords = (
  rawCoords: any[]
): turf.FeatureCollection<turf.Polygon> | null => {
  try {
    const features = rawCoords.map((polyCoords: any) =>
      turf.polygon(polyCoords)
    );
    return turf.featureCollection(
      features
    ) as turf.FeatureCollection<turf.Polygon>;
  } catch (error) {
    console.warn(
      "snapPlotsToAbLine: could not reconstruct FeatureCollection",
      error
    );
    return null;
  }
};

const updateMarkersAfterSnap = (
  target: SnapRenderablePlotOptions,
  translatedFC: turf.FeatureCollection<turf.Polygon>
): void => {
  const centerMarker = markersForTrialPlot[target.centerMarkerId];
  const translatedCenter = turf.center(translatedFC).geometry.coordinates as [
    number,
    number
  ];
  centerMarker.setLngLat(translatedCenter);

  const rotMarker = markersForTrialPlot[target.rotationMarkerId];
  if (rotMarker && translatedFC.features.length > 0) {
    const nwCorner = translatedFC.features[0].geometry.coordinates[0][1];
    rotMarker.setLngLat(nwCorner as [number, number]);
  }
};

const applySnapToRenderablePlot = (
  map: mapboxgl.Map,
  target: SnapRenderablePlotOptions,
  abCoords: [number, number][],
  abBearing: number,
  perpendicularBearing: number,
  guideOffsetsKm: number[]
): void => {
  const source = map.getSource(target.sourceId) as
    | mapboxgl.GeoJSONSource
    | undefined;

  if (!validateSnapTargetInputs(target, source)) {
    return;
  }

  const currentFC = buildFeatureCollectionFromCoords(target.rawCoords!);
  if (!currentFC) {
    return;
  }

  try {
    const centerMarker = markersForTrialPlot[target.centerMarkerId];
    const pivot = centerMarker.getLngLat().toArray() as [number, number];
    const translatedFC = snapFeatureCollectionToGuides(
      currentFC,
      pivot,
      abCoords,
      abBearing,
      perpendicularBearing,
      guideOffsetsKm
    );

    source!.setData(translatedFC as any);
    target.setCoordinates(
      translatedFC.features.map((feature) => feature.geometry.coordinates)
    );

    updateMarkersAfterSnap(target, translatedFC);
    target.onAfterSnap?.(translatedFC);
  } catch (error) {
    console.warn("snapPlotsToAbLine: rotation error", error);
  }
};

const hasBorderCollinearWithGuides = (
  grid: turf.FeatureCollection<turf.Polygon>,
  guideOffsetsKm: number[],
  originCoord: [number, number],
  perpendicularBearing: number
): boolean => {
  if (guideOffsetsKm.length === 0) return false;

  const band = getPlotBorderDistanceBandKm(grid);
  if (!isFinite(band.min) || !isFinite(band.max)) return false;

  let minOffset = Number.POSITIVE_INFINITY;
  let maxOffset = Number.NEGATIVE_INFINITY;
  band.points.forEach((coord) => {
    const offset = getSignedPerpendicularDistanceKm(
      originCoord,
      coord,
      perpendicularBearing
    );
    if (offset < minOffset) minOffset = offset;
    if (offset > maxOffset) maxOffset = offset;
  });

  const toleranceKm = COLLINEAR_DISTANCE_TOLERANCE_METERS / 1000;
  return guideOffsetsKm.some(
    (guideOffset) =>
      Math.abs(guideOffset - minOffset) <= toleranceKm ||
      Math.abs(guideOffset - maxOffset) <= toleranceKm
  );
};

/**
 * Check all trial plots against the current AB line (reads live mutation-updated state)
 * and fire the alignment callback.
 * Works both after plot rotation and after AB line drag because both drag handlers
 * mutate coordinates directly on the objects that moduleNewTrialState holds references to.
 */
const checkAndReportAlignment = (): void => {
  if (!alignmentChangeCallback) return;
  if (!moduleNewTrialState) return;
  if (!moduleMap) return;

  const plots = moduleNewTrialState.trial_plots;
  // No plots yet – nothing to check
  if (!plots || plots.length === 0) return;

  const abLine =
    moduleNewTrialState.ab_line?.[getAlignmentAbLineIndex(moduleNewTrialState)];
  const abCoords = abLine?.geojson?.geometry?.coordinates;
  if (!abCoords || abCoords.length < 2) return;
  // Guard against partially-initialised coordinates (e.g. null/empty entries
  // that occur briefly when all AB lines are deleted and a new one is added)
  const [c0, c1] = abCoords;
  if (
    !Array.isArray(c0) ||
    c0.length < 2 ||
    !Array.isArray(c1) ||
    c1.length < 2
  )
    return;

  const abBearing = turf.bearing(turf.point(c0), turf.point(c1));
  const perpendicularBearing = (((abBearing + 90) % 360) + 360) % 360;
  const guideLines = getAlignmentGuideLines(moduleNewTrialState, moduleMap);
  const guideOffsetsKm = getGuideLineOffsetsKm(
    guideLines,
    c0 as [number, number],
    perpendicularBearing
  );

  const anyMisaligned = plots.some((trialItem) => {
    // Strip plots are aligned to strip lines, not the AB line – skip them
    if (trialItem?.plot?.isStripPlot) return false;
    // coordinates = array of per-cell polygon coordinate sets
    // each cell = [ [ [lng,lat], [lng,lat], ... ] ]  (turf polygon format)
    const coordSets = trialItem?.plot?.geojson?.geometry?.coordinates as any[];
    if (!coordSets || coordSets.length === 0) return false;
    try {
      const features = coordSets
        .map((polyCoords: any) => {
          try {
            return turf.polygon(polyCoords);
          } catch {
            return null;
          }
        })
        .filter((feature): feature is turf.Feature<turf.Polygon> => !!feature);
      if (features.length === 0) return false;

      const grid = turf.featureCollection(features);

      // Fallback to legacy angular check if guide lines are unavailable.
      if (guideOffsetsKm.length === 0) {
        const firstRing: number[][] = coordSets[0][0];
        if (!firstRing || firstRing.length < 2) return false;
        const edgeBearing = turf.bearing(
          turf.point(firstRing[0]),
          turf.point(firstRing[1])
        );
        return bearingDiff(edgeBearing, abBearing) > ALIGNMENT_TOLERANCE_DEG;
      }

      const isCollinear = hasBorderCollinearWithGuides(
        grid,
        guideOffsetsKm,
        c0 as [number, number],
        perpendicularBearing
      );

      return !isCollinear;
    } catch {
      return false;
    }
  });

  alignmentChangeCallback(anyMisaligned);
};

/**
 * Exported entry-point for external callers (e.g. AB-line drag handler in line.ts).
 * Checks whether any plot is misaligned with the AB line and fires the callback.
 */
export const checkAlignmentWithCurrentState = (): void => {
  checkAndReportAlignment();
};

/**
 * Snap all trial plot groups to the current AB line bearing.
 * Call this when the user clicks "Snap to A/B line".
 */
export const snapPlotsToAbLine = (
  newTrialState: NewTrialStateInterface,
  setNewTrialState: (updater: (prev: any) => any) => void
): void => {
  if (!moduleMap) return;
  const map = moduleMap;

  if (!newTrialState.ab_line || newTrialState.ab_line.length === 0) return;
  const firstAb = newTrialState.ab_line[getAlignmentAbLineIndex(newTrialState)];
  const abCoords = firstAb?.geojson?.geometry?.coordinates;
  if (!abCoords || abCoords.length < 2) return;

  const abBearing = turf.bearing(
    turf.point(abCoords[0]),
    turf.point(abCoords[1])
  );
  const perpendicularBearing = (((abBearing + 90) % 360) + 360) % 360;
  const guideLines = getAlignmentGuideLines(newTrialState, map);
  const guideOffsetsKm = getGuideLineOffsetsKm(
    guideLines,
    abCoords[0] as [number, number],
    perpendicularBearing
  );

  newTrialState.trial_plots?.forEach((trialItem: TrialPlot) => {
    // Strip plots are managed by the strip system – skip them
    if (trialItem?.plot?.isStripPlot) return;
    const trialPlotId = trialItem?.plot?.id as number;
    const gridSourceId = `grid-of-trial-plots-${trialPlotId}`;

    applySnapToRenderablePlot(
      map,
      {
        sourceId: gridSourceId,
        centerMarkerId: `envelope-center-marker-${trialPlotId}`,
        rotationMarkerId: `rotation-marker-${trialPlotId}`,
        rawCoords: trialItem?.plot?.geojson?.geometry?.coordinates,
        setCoordinates: (coordinates) => {
          trialItem.plot.geojson.geometry.coordinates = coordinates;
        },
        onAfterSnap: (translatedFC) => {
          updateGroupLabelPosition(
            map,
            gridSourceId,
            translatedFC,
            newTrialState,
            trialPlotId,
            "",
            [0, -0.2],
            14
          );
          updatePlotLabelMarkersPosition(translatedFC, trialPlotId);
        },
      },
      abCoords as [number, number][],
      abBearing,
      perpendicularBearing,
      guideOffsetsKm
    );

    trialItem.replicants?.forEach((replicant, cloneIndex) => {
      const replicantSourceId = `cloned-grid-of-trial-plots-${cloneIndex}-${trialPlotId}-${
        replicant.id as number
      }`;

      applySnapToRenderablePlot(
        map,
        {
          sourceId: replicantSourceId,
          centerMarkerId: `envelope-center-marker-replicant-${
            replicant.id as number
          }`,
          rotationMarkerId: `rotation-marker-replicant-${
            replicant.id as number
          }`,
          rawCoords: replicant?.geojson?.geometry?.coordinates,
          setCoordinates: (coordinates) => {
            replicant.geojson.geometry.coordinates = coordinates;
          },
          onAfterSnap: (translatedFC) => {
            updateGroupLabelPosition(
              map,
              replicantSourceId,
              translatedFC,
              newTrialState,
              trialPlotId,
              "'",
              [0, 0],
              12
            );
            updateReplicantPlotLabels(
              map,
              translatedFC,
              cloneIndex,
              trialPlotId
            );
          },
        },
        abCoords as [number, number][],
        abBearing,
        perpendicularBearing,
        guideOffsetsKm
      );
    });
  });

  // Trigger a React state refresh so downstream consumers see updated coordinates
  setNewTrialState((prev: any) => ({
    ...prev,
    trial_plots: prev.trial_plots.map((tp: TrialPlot) => ({
      ...tp,
      plot: { ...tp.plot },
      replicants: tp.replicants?.map((replicant: Replicants) => ({
        ...replicant,
      })),
    })),
  }));

  // Dismiss the banner
  if (alignmentChangeCallback) {
    alignmentChangeCallback(false);
  }
};

// Cache to prevent unnecessary strip line reprocessing
let stripLinesCache = new Set<string>();

// Track previous step to clear cache when step changes
let previousStep: number | undefined;

// Function to clear strip lines cache
export const clearStripLinesCache = () => {
  stripLinesCache.clear();
};

// Function to clear strip lines from both cache and map
export const clearStripLinesFromMap = (
  mapRef?: React.MutableRefObject<mapboxgl.Map | null>
) => {
  // Clear the cache first
  stripLinesCache.clear();

  // If mapRef is provided, use it directly
  if (mapRef?.current) {
    clearStripsFromMapInstance(mapRef.current, mapRef);
  } else {
    // Try to find mapbox instances in the DOM as fallback

    try {
      // Look for mapbox containers in the DOM
      const mapContainers = document.querySelectorAll(".mapboxgl-map");
      mapContainers.forEach((container) => {
        const mapInstance = (container as any)?._mapboxMap;
        if (mapInstance) {
          clearStripsFromMapInstance(mapInstance, { current: mapInstance });
        }
      });
    } catch (error) {
      console.warn("⚠️ Could not access map instances from DOM:", error);
    }
  }
};

// Helper function to clear strips from a specific map instance
const clearStripsFromMapInstance = (
  mapInstance: mapboxgl.Map,
  mapRef: React.MutableRefObject<mapboxgl.Map | null>
) => {
  try {
    // Remove strip lines with common source IDs using removeStripLinesFromMap function
    const commonSourceIds = ["strip-lines", "strip-src-0"];
    commonSourceIds.forEach((sourceId) => {
      try {
        removeStripLinesFromMap(mapRef, sourceId);
      } catch (error) {
        console.warn(
          `⚠️ Could not remove strip lines for source ${sourceId}:`,
          error
        );
      }
    });

    // Also remove any layers/sources with "strip" in the name as fallback
    const style = mapInstance.getStyle();
    if (style?.layers) {
      const stripLayers = style.layers.filter(
        (layer: any) =>
          layer.id.includes("strip") ||
          layer.id.includes("Strip") ||
          layer.id.includes("layer-strip") ||
          layer.source?.includes("strip")
      );

      stripLayers.forEach((layer: any) => {
        try {
          if (mapInstance.getLayer(layer.id)) {
            mapInstance.removeLayer(layer.id);
          }
        } catch (error) {
          console.warn(`⚠️ Could not remove layer ${layer.id}:`, error);
        }
      });
    }

    // Remove sources containing "strip"
    if (style?.sources) {
      const stripSources = Object.keys(style.sources).filter(
        (sourceId) => sourceId.includes("strip") || sourceId.includes("Strip")
      );

      stripSources.forEach((sourceId) => {
        try {
          if (mapInstance.getSource(sourceId)) {
            mapInstance.removeSource(sourceId);
          }
        } catch (error) {
          console.warn(`⚠️ Could not remove source ${sourceId}:`, error);
        }
      });
    }
  } catch (error) {
    console.error("❌ Error clearing strips from map instance:", error);
  }
};

const updateABWidth = (
  abItem: AbLine,
  sourceId: string,
  map: mapboxgl.Map,
  bboxOld: any,
  polygonGeoJSON: PolygonGeoJSON,
  trialProtocolData?: any
) => {
  if (abItem.grid_width === 0) return;
  // turf.feature (not turf.polygon) keeps MultiPolygon field geometries intact so
  // the machinery grid is clipped inside every sub-polygon.
  const turfPolygon = turf.feature(
    polygonGeoJSON.features[0].geometry
  ) as turf.Feature<turf.Polygon | turf.MultiPolygon>;

  const layerId = `grid-line-layer-${abItem.id ?? ""}`;
  const sourceIdGrid = `grid-layer-${abItem.id ?? ""}`;

  drawMachineryGridPure(
    map,
    turfPolygon,
    abItem.geojson.geometry.coordinates[0],
    abItem.geojson.geometry.coordinates[1],
    sourceIdGrid,
    layerId,
    { spacing: abItem.grid_width, drawGreyLines: true }
  );

  // Keep the machinery grid (main + grey) as the BOTTOM-most trial layer so
  // EVERYTHING — field, plot/cell rate colours, strip lines and labels — draws
  // on top of it and stays visible. updateABWidth is fired by a separate effect
  // that can run after those layers, so move the grid under the lowest of them.
  // "polygon-fill" (the field) is added at map load, so it is the lowest custom
  // layer; fall back to the first plot fill (`grid-of-trial-plots-*`) if absent.
  const anchorLayerId = map.getLayer("polygon-fill")
    ? "polygon-fill"
    : (map.getStyle()?.layers ?? []).find(
        (styleLayer) =>
          typeof (styleLayer as { source?: unknown }).source === "string" &&
          (styleLayer as { source: string }).source.startsWith(
            "grid-of-trial-plots-"
          )
      )?.id;
  if (anchorLayerId) {
    try {
      const grayLayerId = `${layerId}-gray`;
      // Main grid just below the anchor, grey just below the main grid.
      if (map.getLayer(layerId)) map.moveLayer(layerId, anchorLayerId);
      if (map.getLayer(grayLayerId)) map.moveLayer(grayLayerId, layerId);
    } catch {
      /* layer ordering is best-effort */
    }
  }
};

const checkPlotBoundaries = (
  geoJsonData: any,
  plotsValidationData?: PlotsValidationData,
  setPlotsValidationData?: any,
  newTrialState?: any
) => {
  // ─── Plot-to-plot intersection check ────────────────────────────────────────
  // This MUST run unconditionally – it does not depend on field boundary geometry.
  // Moving it here ensures it is never skipped by early-returns below that are
  // only relevant for the field-boundary check.
  const checkPlotIntersections = (
    trialState: { trial_plots: TrialPlot[] },
    setValidationData: React.Dispatch<React.SetStateAction<PlotsValidationData>>
  ) => {
    if (!trialState?.trial_plots || !setValidationData) return;

    const newIntersectingPairs: [number, number][] = [];
    const allPlots: any = [];

    /**
     * Expand a plot's MultiPolygon coordinates into individual Polygon cell features.
     *
     * Why cell-level instead of MultiPolygon-level?
     * ─────────────────────────────────────────────
     * In Turf v6, `booleanContains(MultiPolygonA, B)` checks whether ANY SINGLE
     * polygon cell of A contains all of B.  When a plot is dragged inside another
     * plot but straddles several cells of that plot (the common case), no single
     * cell of A contains all of B, so the check wrongly returns false.
     *
     * Working at the individual cell (Polygon) level avoids this limitation.
     * Two plots overlap if and only if at least one cell of plot A shares area
     * with at least one cell of plot B.
     */
    const getCells = (plot: any): turf.Feature<turf.Polygon>[] => {
      try {
        const coords = plot?.geojson?.geometry?.coordinates;
        // coords[i] = polygon ring array for cell i: [[lng,lat],...]
        // A Point geometry stores coords as [lng, lat] — coords[0] is a number.
        if (!coords || !Array.isArray(coords) || coords.length === 0) return [];
        if (!Array.isArray(coords[0])) return []; // Point placeholder
        return coords
          .map((polygonCoords: any) => {
            try {
              return turf.polygon(polygonCoords);
            } catch {
              return null;
            }
          })
          .filter(Boolean) as turf.Feature<turf.Polygon>[];
      } catch {
        return [];
      }
    };

    /**
     * Returns true when two individual cell Polygons share ANY interior area.
     * Covers three distinct overlap cases:
     *   1. Partial overlap  — borders cross each other
     *   2. Containment      — cell A entirely contains cell B
     *   3. Identity/Within  — cell A is entirely within cell B
     */
    const cellsShareArea = (
      a: turf.Feature<turf.Polygon>,
      b: turf.Feature<turf.Polygon>
    ): boolean => {
      try {
        return (
          turf.booleanOverlap(a, b) ||
          turf.booleanContains(a, b) ||
          turf.booleanWithin(a, b)
        );
      } catch {
        return false;
      }
    };

    // Flatten all plots and replicants into a single array
    trialState.trial_plots.forEach((trialPlot) => {
      allPlots.push(trialPlot.plot);
      if (trialPlot.replicants) {
        allPlots.push(...trialPlot.replicants);
      }
    });

    // Pre-compute individual cell polygons for every plot/replicant
    const plotCells: turf.Feature<turf.Polygon>[][] = allPlots.map(getCells);

    // Check every plot pair — two plots overlap when ANY cell pair shares area
    for (let i = 0; i < allPlots.length; i++) {
      for (let j = i + 1; j < allPlots.length; j++) {
        if (plotCells[i].length === 0 || plotCells[j].length === 0) continue;

        let hasOverlap = false;
        outer: for (const cellI of plotCells[i]) {
          for (const cellJ of plotCells[j]) {
            if (cellsShareArea(cellI, cellJ)) {
              hasOverlap = true;
              break outer;
            }
          }
        }

        if (hasOverlap) {
          newIntersectingPairs.push([allPlots[i].id, allPlots[j].id]);
        }
      }
    }

    setValidationData((prevState: PlotsValidationData) => {
      if (
        JSON.stringify(prevState.intersectingPairs) !==
        JSON.stringify(newIntersectingPairs)
      ) {
        return {
          ...prevState,
          intersectingPairs: newIntersectingPairs,
        };
      }
      return prevState;
    });
  };

  // Always run intersection check — independent of field geometry
  checkPlotIntersections(newTrialState, setPlotsValidationData);

  // ─── Field-boundary check ────────────────────────────────────────────────────
  // The remaining logic requires a valid simple-Polygon field boundary.
  // Early returns below only skip the field-boundary part, NOT the intersection check above.
  if (!geoJsonData.features) return;
  // Expand the field into its sub-polygons. A plain Polygon yields one entry; a
  // MultiPolygon yields one per detached parcel. A plot/cell is "inside the field"
  // when it lies within ANY sub-polygon, so plots may be placed in either parcel.
  const fieldPolygons = getSubPolygons(
    geoJsonData.features[0].geometry as turf.Geometry
  );
  if (fieldPolygons.length === 0) return;

  const checkPolygon = (plotData: any, plotId: any) => {
    const plotCoordinates = plotData?.geojson?.geometry?.coordinates;

    const allPolygonsWithin =
      plotCoordinates && Array.isArray(plotCoordinates)
        ? plotCoordinates.every((polygonCoords: turf.Position[][]) => {
            if (!polygonCoords || !Array.isArray(polygonCoords)) return false;

            // Validate that each coordinate ring has at least 4 points (minimum for a valid polygon)
            const hasValidRings = polygonCoords.every((ring) => {
              if (!Array.isArray(ring) || ring.length < 4) {
                console.warn(
                  "⚠️ Invalid polygon ring - needs at least 4 points:",
                  { ringLength: ring?.length, ring }
                );
                return false;
              }
              return true;
            });

            if (!hasValidRings) {
              console.warn(
                "⚠️ Skipping polygon bounds check due to invalid rings"
              );
              return true; // Skip validation for invalid polygons to avoid crashes
            }

            try {
              const plotPolygon = turf.polygon(polygonCoords);
              // Valid when the cell is fully within at least one sub-polygon.
              return fieldPolygons.some((fieldPoly) =>
                turf.booleanWithin(plotPolygon, fieldPoly)
              );
            } catch (error) {
              console.warn("Error checking polygon bounds:", error);
              return false;
            }
          })
        : false;

    const exists =
      plotsValidationData?.polygonsNotInsideField?.includes(plotId);

    if (allPolygonsWithin) {
      if (exists) {
        setPlotsValidationData((prevState: any) => {
          const updatedPolygons = prevState.polygonsNotInsideField?.filter(
            (item: any) => item !== plotId
          );
          if (
            JSON.stringify(updatedPolygons) !==
            JSON.stringify(prevState.polygonsNotInsideField)
          ) {
            return {
              ...prevState,
              polygonsNotInsideField: updatedPolygons,
            };
          }
          return prevState;
        });
      }
    } else {
      if (!exists) {
        setPlotsValidationData((prevState: any) => ({
          ...prevState,
          polygonsNotInsideField: [
            ...(prevState.polygonsNotInsideField || []),
            plotId,
          ],
        }));
      }
    }
  };

  newTrialState?.trial_plots?.forEach((trialItem: TrialPlot) => {
    // Check main plot
    checkPolygon(trialItem.plot, trialItem.plot.id);

    // Check replicants
    trialItem.replicants?.forEach((replicant) => {
      checkPolygon(replicant, replicant.id);
    });
  });
};

/**
 * Snaps the leading edge of a rotated plot grid to the nearest machinery grid
 * line that runs parallel to the AB line.
 *
 * Grid lines are spaced `gridWidthMeters` metres apart along the axis that is
 * perpendicular to the AB line bearing (`abBearing + 90°`), measured from
 * `abOriginCoord`.  The function finds the minimum perpendicular projection of
 * any grid vertex, rounds it to the nearest multiple of the grid spacing, then
 * translates the entire feature-collection by the difference.
 *
 * @param gridGeoJSON       Rotated FeatureCollection of plot cell polygons.
 * @param abOriginCoord     First coordinate of the AB line [lng, lat].
 * @param abBearing         Bearing of the AB line in degrees.
 * @param gridWidthMeters   Machinery grid spacing in metres (i.e. `grid_width`).
 * @returns                 The (possibly translated) FeatureCollection, or the
 *                          original collection unchanged if inputs are invalid.
 */
export const snapGridToAbLine = (
  gridGeoJSON: turf.FeatureCollection<turf.Polygon>,
  abOriginCoord: [number, number],
  abBearing: number,
  gridWidthMeters: number,
  options?: { mode?: "nearestGrid" | "abOnly" }
): turf.FeatureCollection<turf.Polygon> => {
  if (!gridGeoJSON || gridGeoJSON.features.length === 0) return gridGeoJSON;
  if (
    !abOriginCoord ||
    !Number.isFinite(abBearing) ||
    !Number.isFinite(gridWidthMeters) ||
    gridWidthMeters <= 0
  )
    return gridGeoJSON;

  const perpBearing = (((abBearing + 90) % 360) + 360) % 360;
  const gridSpacingKm = gridWidthMeters / 1000;
  const abOrigin = turf.point(abOriginCoord);

  let minPerpDist = Infinity;
  for (const feature of gridGeoJSON.features) {
    const ring = feature.geometry.coordinates[0];
    for (const coord of ring) {
      const pt = turf.point(coord as [number, number]);
      const dist = turf.rhumbDistance(abOrigin, pt, { units: "kilometers" });
      const bearingToPt = turf.rhumbBearing(abOrigin, pt);
      const angleDiff = ((bearingToPt - perpBearing + 540) % 360) - 180;
      const perpComp = dist * Math.cos((angleDiff * Math.PI) / 180);
      if (perpComp < minPerpDist) minPerpDist = perpComp;
    }
  }

  if (!isFinite(minPerpDist)) return gridGeoJSON;

  const snappedDist =
    options?.mode === "abOnly"
      ? 0
      : Math.round(minPerpDist / gridSpacingKm) * gridSpacingKm;
  const shiftKm = snappedDist - minPerpDist;

  if (Math.abs(shiftKm) <= 1e-9) return gridGeoJSON;

  return turf.transformTranslate(gridGeoJSON, shiftKm, perpBearing, {
    units: "kilometers",
  }) as turf.FeatureCollection<turf.Polygon>;
};

const getRhumbOffsetPoint = (
  originCoord: [number, number],
  eastMeters: number,
  northMeters: number
): [number, number] => {
  let point = turf.point(originCoord);

  if (eastMeters !== 0) {
    point = turf.rhumbDestination(
      point,
      Math.abs(eastMeters) / 1000,
      eastMeters >= 0 ? 90 : 270,
      { units: "kilometers" }
    );
  }

  if (northMeters !== 0) {
    point = turf.rhumbDestination(
      point,
      Math.abs(northMeters) / 1000,
      northMeters >= 0 ? 0 : 180,
      { units: "kilometers" }
    );
  }

  return point.geometry.coordinates as [number, number];
};

export const buildAxisAlignedPlotGrid = (
  startingPoint: [number, number],
  rows: number,
  columns: number,
  plotWidthMeters: number,
  plotHeightMeters: number
): turf.FeatureCollection<turf.Polygon> => {
  const features: turf.Feature<turf.Polygon>[] = [];

  for (let rowIndex = INDEX_ZERO; rowIndex < rows; rowIndex++) {
    const southOffsetMeters = -rowIndex * plotHeightMeters;
    const northOffsetMeters = southOffsetMeters + plotHeightMeters;

    for (let columnIndex = INDEX_ZERO; columnIndex < columns; columnIndex++) {
      const westOffsetMeters = columnIndex * plotWidthMeters;
      const eastOffsetMeters = westOffsetMeters + plotWidthMeters;

      const southWest = getRhumbOffsetPoint(
        startingPoint,
        westOffsetMeters,
        southOffsetMeters
      );
      const northWest = getRhumbOffsetPoint(
        startingPoint,
        westOffsetMeters,
        northOffsetMeters
      );
      const northEast = getRhumbOffsetPoint(
        startingPoint,
        eastOffsetMeters,
        northOffsetMeters
      );
      const southEast = getRhumbOffsetPoint(
        startingPoint,
        eastOffsetMeters,
        southOffsetMeters
      );

      features.push(
        turf.polygon([[southWest, northWest, northEast, southEast, southWest]])
      );
    }
  }

  return turf.featureCollection(features);
};

/** True when a single plot cell lies completely inside at least one field parcel. */
const isFeatureWithinAnyPolygon = (
  feature: turf.Feature<turf.Polygon>,
  fieldPolygons: Array<turf.Feature<turf.Polygon>>
): boolean => {
  return fieldPolygons.some((fieldPoly) => {
    try {
      return turf.booleanWithin(feature, fieldPoly);
    } catch {
      return false;
    }
  });
};

/** Number of grid cells that lie completely inside the field (any parcel). */
const countCellsInsideField = (
  grid: turf.FeatureCollection<turf.Polygon>,
  fieldPolygons: Array<turf.Feature<turf.Polygon>>
): number => {
  return grid.features.reduce(
    (count, feature) =>
      isFeatureWithinAnyPolygon(feature, fieldPolygons) ? count + 1 : count,
    0
  );
};

/**
 * True when the WHOLE grid lies completely inside a SINGLE field parcel.
 * (A MultiPolygon field is a set of parcels; a plot block must sit inside one of
 * them, not straddle the gap between two.)
 */
const isGridInsideSingleParcel = (
  grid: turf.FeatureCollection<turf.Polygon>,
  fieldPolygons: Array<turf.Feature<turf.Polygon>>
): boolean => {
  if (grid.features.length === 0) return false;
  return fieldPolygons.some((fieldPoly) =>
    grid.features.every((cell) => {
      try {
        return turf.booleanWithin(cell, fieldPoly);
      } catch {
        return false;
      }
    })
  );
};

/** True when two plot cells share any interior area (partial / contained / within). */
const cellsOverlap = (
  a: turf.Feature<turf.Polygon>,
  b: turf.Feature<turf.Polygon>
): boolean => {
  try {
    return (
      turf.booleanOverlap(a, b) ||
      turf.booleanContains(a, b) ||
      turf.booleanWithin(a, b)
    );
  } catch {
    return false;
  }
};

/** True when any cell of the grid overlaps any already-placed plot footprint. */
const gridOverlapsFootprints = (
  grid: turf.FeatureCollection<turf.Polygon>,
  footprints: Array<turf.Feature<turf.Polygon>>
): boolean => {
  if (footprints.length === 0) return false;
  return grid.features.some((cell) =>
    footprints.some((footprint) => cellsOverlap(cell, footprint))
  );
};

/**
 * Collect the cell polygons of every OTHER non-strip plot (and its replicants)
 * so a newly-placed / edited plot can be positioned without landing on top of
 * an existing one. The plot currently being (re)generated is excluded by id.
 */
const collectExistingPlotFootprints = (
  state: NewTrialStateInterface,
  currentPlotId: number
): Array<turf.Feature<turf.Polygon>> => {
  const footprints: Array<turf.Feature<turf.Polygon>> = [];

  const addCoords = (coordsList: any): void => {
    if (!Array.isArray(coordsList) || !Array.isArray(coordsList[0])) return; // skip [0,0] point placeholders
    coordsList.forEach((polygonCoords: any) => {
      try {
        const poly = turf.polygon(polygonCoords);
        if (turf.area(poly) > 0) footprints.push(poly);
      } catch {
        // ignore malformed cell
      }
    });
  };

  state.trial_plots?.forEach((item: any) => {
    if (item?.plot?.isStripPlot) return;
    if (item?.plot?.id === currentPlotId) return;
    addCoords(item?.plot?.geojson?.geometry?.coordinates);
    item?.replicants?.forEach((replicant: any) =>
      addCoords(replicant?.geojson?.geometry?.coordinates)
    );
  });

  return footprints;
};

/**
 * Candidate centre points spread across a parcel, used to search for a free,
 * fully-inside placement for a new plot block. Random interior points give
 * spatial variety (so plots don't stack), and a guaranteed-interior anchor is
 * always appended as a last-resort candidate.
 */
const generatePlacementCandidatePoints = (
  placementArea: any,
  count: number
): Array<[number, number]> => {
  const points: Array<[number, number]> = [];

  try {
    const randomPoints = turf.randomPoint(count, {
      bbox: turf.bbox(placementArea),
    });
    const within = turf.pointsWithinPolygon(randomPoints, placementArea);
    within.features.forEach((feature: any) => {
      const coord = feature?.geometry?.coordinates;
      if (Array.isArray(coord) && coord.length >= 2) {
        points.push([coord[0], coord[1]]);
      }
    });
  } catch {
    // ignore – anchor fallback below still provides a candidate
  }

  try {
    const anchor = turf.pointOnFeature(placementArea).geometry.coordinates as [
      number,
      number
    ];
    points.push(anchor);
  } catch {
    // ignore
  }

  return points;
};

const updateStripsData = (
  map: mapboxgl.Map | null,
  newTrialState: NewTrialStateInterface,
  polygonGeoJSON: PolygonGeoJSON,
  setNewTrialState: any,
  currentStep?: number,
  setStripsData?: any,
  stripsData?: any
): void => {
  // setNewTrialState((prevState: NewTrialStateInterface) => {
  //   const updatedState = { ...prevState };
  //   if (currentStep !== 1) {
  //     updatedState.stripLines = newTrialState.stripLines?.map((strip,index) => ({
  //       ...strip,
  //       gridLineIndex: prevState.stripLines?.find((i) => i === strip)?.gridLineIndex? + 1 :
  //         index + 1
  //     }));
  //   }

  //   return updatedState;
  // });

  const updatedData = [{ gridLineIndex: 0 }];
  // setStripsData(updatedData);

  // setNewTrialState((prevState: NewTrialStateInterface) => {
  //   const updatedState = { ...prevState, stripsData: updatedData };
  //   return updatedState;
  // });
};

/**
 * Map component utility hook that provides marker creation, map update orchestration,
 * AB line width updates, plot boundary validation, and strip line management helpers.
 *
 * @returns An API with functions to create custom marker elements, update map layers/data,
 * update AB line widths, validate plot boundaries, update strip data, and clear strip lines.
 */
export const useMapComponent = (): {
  createCustomMarkerElement: (iconUrl: string) => HTMLDivElement;

  updateMapData: (
    map: mapboxgl.Map | null,
    mapObjectRef: React.MutableRefObject<mapboxgl.Map | null>,
    current: number,
    isFertilizerTabActive: boolean,
    activeRatesTab: string,
    fieldData: FieldDataInterface[],
    newTrialState: NewTrialStateInterface,
    setNewTrialState: any,
    createCustomMarkerElement: (iconUrl: string) => HTMLDivElement,
    geoJsonData: PolygonGeoJSON,
    plotsValidationData: PlotsValidationData,
    setPlotsValidationData: any,
    autozoneRequestStatus: autozoneRequestStatus,
    productivityZonesData: ProductivityZonesInterface,
    isAutozonesLoaded: boolean,
    setIsAutozonesLoaded: any,
    autozoneOpacity: number,
    autozoneColorPalette: string,
    fieldOperationsData: any,
    stripsData: any,
    setStripsData: any,
    updateStripsData: any,
    soilOperationsData: any, // <-- soil data object (GeoJSON or raster)
    ratesDosageParam?: any,
    soilColorPalete?: string,
    soilOpacity?: any,
    soilImageData?: { image_location: string; bbox: number[][] }
  ) => void;
  updateABWidth: (
    abItem: AbLine,
    sourceId: string,
    map: mapboxgl.Map,
    bbox: any,
    polygonGeoJSON: PolygonGeoJSON,
    trialProtocolData?: any
  ) => void;

  checkPlotBoundaries: (
    newTrialState: any,
    geoJsonData: any,
    plotsValidationData: PlotsValidationData,
    setPlotsValidationData: any
  ) => void;
  updateStripsData: (
    map: mapboxgl.Map | null,
    newTrialState: NewTrialStateInterface,
    polygonGeoJSON: PolygonGeoJSON,
    setNewTrialState: any,
    currentStep?: number,
    setStripsData?: any,
    stripsData?: any
  ) => void;
  clearStripLinesCache: () => void;
  clearStripLinesFromMap: (
    mapRef?: React.MutableRefObject<mapboxgl.Map | null>
  ) => void;
} => {
  const createCustomMarkerElement = (
    iconUrl: string,
    abItemid?: number
  ): HTMLDivElement => {
    const markerElement = document.createElement("div");
    markerElement.className = "custom-marker";
    markerElement.style.backgroundImage = `url(${iconUrl})`;
    markerElement.style.width = "30px";
    markerElement.style.height = "30px";
    markerElement.style.backgroundSize = "cover";
    return markerElement;
  };

  const { ratesDosage, trialProtocolData, bioApplicationIndex } =
    useNewTrialState();
  const { getAbLineColor } = usePlotCardState();
  const trialProducts =
    trialProtocolData?.product_details?.seeds?.products ?? [];

  // Flag to prevent multiple simultaneous executions
  let isUpdatingMapData = false;

  const updateMapData = (
    map: mapboxgl.Map | null,
    mapObjectRef: React.MutableRefObject<mapboxgl.Map | null>,
    current: number,
    isFertilizerTabActive: boolean,
    activeRatesTab: string,
    fieldData: FieldDataInterface[],
    newTrialState: NewTrialStateInterface,
    setNewTrialState: any,
    createCustomMarkerElement: (iconUrl: string) => HTMLDivElement,
    geoJsonData: PolygonGeoJSON,
    plotsValidationData: PlotsValidationData,
    setPlotsValidationData: any,
    autozoneRequestStatus: autozoneRequestStatus,
    productivityZonesData: ProductivityZonesInterface,
    isAutozonesLoaded: boolean,
    setIsAutozonesLoaded: any,
    autozoneOpacity: number,
    autozoneColorPalette: string,
    fieldOperationsData: any,
    stripsData: any,
    setStripsData: any,
    updateStripsData: any,
    soilOperationsData: any, // <-- soil data object (GeoJSON or raster)
    ratesDosageParam?: any,
    soilColorPalete?: string,
    soilOpacity?: any,
    soilImageData?: { image_location: string; bbox: number[][] }
  ): void => {
    // Track step changes to clear cache when step changes
    if (previousStep !== undefined && previousStep !== current) {
      stripLinesCache.clear();
    }
    previousStep = current;

    // Use passed parameter or fallback to hook (for backward compatibility)
    const currentRatesDosage = ratesDosageParam || ratesDosage;

    // Prevent multiple simultaneous executions
    if (isUpdatingMapData) {
      return;
    }

    isUpdatingMapData = true;

    // Store module-level references for snapPlotsToAbLine and checkAndReportAlignment
    if (map) moduleMap = map;
    moduleNewTrialState = newTrialState;

    // Perform an initial alignment check when entering plot-design (step 1)
    // so the banner reflects the current alignment state without requiring a drag event.
    if (current === 1) {
      checkAndReportAlignment();
    }

    try {
      if (current === 1 || current === 2 || current === 3) {
        if (newTrialState.buffer_zone) {
          addBufferZoneLayer(map, fieldData, newTrialState, geoJsonData);
        }

        if (newTrialState.ab_line) {
          addABLinesToMap(
            map,
            newTrialState,
            setNewTrialState,
            geoJsonData,
            createCustomMarkerElement,
            getAbLineColor,
            current,
            trialProtocolData
          );
        }
        // create function ab_state_cahnge =>
        if (newTrialState.trial_plots) {
          addTrialPlotToMap(
            map,
            newTrialState,
            geoJsonData,
            createCustomMarkerElement,
            plotsValidationData,
            setPlotsValidationData,
            checkPlotBoundaries
          );
          // Check if we're in strips mode based on consolidated stripsData
          const isStripsMode = newTrialState.stripsData?.isStripMode === true;

          // strips-flow-start : call the relevant addStripLinesToMap function from utils (normal and jitendra's versions in code)

          if (newTrialState?.ab_line && newTrialState?.ab_line.length > 0) {
            // Check if strips should be cleared from the map
            // Only draw strips once the user has clicked Apply in the StripsPopup
            if (
              !newTrialState.stripsData ||
              !newTrialState.stripsData.offsets ||
              newTrialState.stripsData.offsets.length === 0 ||
              !newTrialState.stripsData.appliedFromPopup
            ) {
              const mapRef = { current: map };
              clearStripLinesFromMap(mapRef);
            } else {
              // Get the AB line for strips based on consolidated stripsData
              const selectedAbLineIndex =
                newTrialState.stripsData?.selectedAbLineIndex ?? 0;
              const selectedAbLine =
                newTrialState?.ab_line[selectedAbLineIndex] ||
                newTrialState?.ab_line[0];
              const abLine = selectedAbLine;

              // Only process strip lines if AB line or strips data has changed
              const currentOffsets = newTrialState?.stripsData?.offsets;
              // Include current step in cache key to ensure controls are redrawn when step changes
              const cacheKey = `${abLine.id}-${JSON.stringify(
                currentOffsets
              )}-step${current}`;

              // Check if plots need recalculation (when geometrySource is 'manual' for strip plots)
              const plotsNeedRecalculation = newTrialState.trial_plots?.some(
                (tp: any) =>
                  tp.plot?.isStripPlot && tp.plot?.geometrySource === "manual"
              );

              // Force processing if we're in strips mode but plots are missing strip properties
              const hasValidStripsMode =
                newTrialState.stripsData?.isStripMode === true;
              const plotsMissingStripProperties =
                hasValidStripsMode &&
                newTrialState.trial_plots?.some(
                  (tp: any) => tp.plot && tp.plot.isStripPlot !== true
                );

              if (
                stripLinesCache.has(cacheKey) &&
                !plotsNeedRecalculation &&
                !plotsMissingStripProperties
              ) {
              } else {
                if (plotsNeedRecalculation) {
                  stripLinesCache.delete(cacheKey); // Remove from cache to force recalculation
                }

                if (plotsMissingStripProperties) {
                  stripLinesCache.delete(cacheKey); // Remove from cache to force recalculation

                  // Mark plots as strip plots before processing
                  setNewTrialState((prevState: any) => {
                    const updatedTrialPlots = prevState.trial_plots.map(
                      (trialPlot: any) => {
                        if (!trialPlot.plot.isStripPlot && hasValidStripsMode) {
                          return {
                            ...trialPlot,
                            plot: {
                              ...trialPlot.plot,
                              isStripPlot: true,
                              totalStrips: prevState.stripsData?.count || 2,
                              stripIndices: Array.from(
                                { length: prevState.stripsData?.count || 2 },
                                (_, i) => i
                              ),
                              geometrySource: "manual", // Force recalculation
                            },
                          };
                        }
                        return trialPlot;
                      }
                    );

                    return {
                      ...prevState,
                      trial_plots: updatedTrialPlots,
                    };
                  });
                }

                // Create a ref object that the function expects
                const mapRef = { current: map };

                // Get strip offsets from trial state or use default
                let isAdjusting = false; // Flag to prevent infinite recursion
                const DEFAULT_STRIP_GRID_WIDTH = 100;

                // Async strip line adjustment callback - prevents infinite loops
                const handleStripLineAdjust = (data: {
                  direction: "left" | "right";
                  indexOfStripLine: number;
                  action?: "step" | "snap";
                  snappedDistance?: number;
                }) => {
                  // Prevent multiple simultaneous adjustments
                  if (isAdjusting) {
                    return;
                  }

                  isAdjusting = true;

                  // Use setTimeout to break out of the current execution context and prevent infinite loops
                  setTimeout(() => {
                    try {
                      // Get current offsets directly from trial state
                      const currentOffsets =
                        newTrialState.stripsData?.offsets || [];

                      // Get the selected AB line's width for adjustment step
                      const selectedAbLine =
                        newTrialState.ab_line.find(
                          (line) => line.selectedForStrips
                        ) || newTrialState.ab_line[0];
                      // Use a consistent default if grid_width is null/undefined/0
                      const gridWidth =
                        selectedAbLine?.grid_width &&
                        selectedAbLine.grid_width > 0
                          ? selectedAbLine.grid_width
                          : DEFAULT_STRIP_GRID_WIDTH;
                      const adjustmentStep = gridWidth / 2;

                      const newOffsets = [...currentOffsets];

                      // Resolve target index. For snap actions, map out-of-range indices
                      // to the nearest existing offset so updates are not dropped.
                      let targetIndex = Number.isInteger(data.indexOfStripLine)
                        ? data.indexOfStripLine
                        : -1;

                      if (
                        (targetIndex < 0 || targetIndex >= newOffsets.length) &&
                        data.action === "snap" &&
                        typeof data.snappedDistance === "number" &&
                        Number.isFinite(data.snappedDistance) &&
                        newOffsets.length > 0
                      ) {
                        let nearestOffsetIndex = 0;
                        let nearestDelta = Number.POSITIVE_INFINITY;

                        newOffsets.forEach((offset, index) => {
                          const delta = Math.abs(
                            offset - data.snappedDistance!
                          );
                          if (delta < nearestDelta) {
                            nearestDelta = delta;
                            nearestOffsetIndex = index;
                          }
                        });

                        targetIndex = nearestOffsetIndex;
                      }

                      if (targetIndex < 0 || targetIndex >= newOffsets.length) {
                        isAdjusting = false;
                        return;
                      }

                      // Apply adjustment: either nudge by step or snap to an absolute line distance.
                      if (
                        data.action === "snap" &&
                        typeof data.snappedDistance === "number" &&
                        Number.isFinite(data.snappedDistance)
                      ) {
                        newOffsets[targetIndex] = data.snappedDistance;
                      } else if (data.direction === "left") {
                        newOffsets[targetIndex] -= adjustmentStep;
                      } else {
                        newOffsets[targetIndex] += adjustmentStep;
                      }

                      // Clear cache to force redraw
                      stripLinesCache.clear();

                      // Keep offsets sorted left→right. If a line is dragged past
                      // a neighbour the order changes; re-sorting keeps the band
                      // boundaries and strip numbering (1..N) consistent on the
                      // next recompute.
                      const sortedOffsets = [...newOffsets].sort(
                        (a, b) => a - b
                      );

                      // Only update state - let the next updateMapData call handle the map update
                      setNewTrialState((prevState: any) => ({
                        ...prevState,
                        stripsData: {
                          ...prevState.stripsData,
                          offsets: sortedOffsets,
                        },
                      }));
                    } catch (error) {
                      console.error("❌ Error adjusting strip lines:", error);
                    } finally {
                      isAdjusting = false;
                    }
                  }, 0); // Execute on next tick to break the execution chain
                };

                try {
                  // Get the selected AB line's width to calculate initial offsets
                  // Use a consistent default if grid_width is null/undefined/0
                  const selectedAbLineWidth =
                    abLine.grid_width && abLine.grid_width > 0
                      ? abLine.grid_width
                      : DEFAULT_STRIP_GRID_WIDTH;

                  // Always use offsets from trial state if they exist, otherwise calculate fresh defaults
                  let currentStripOffsets: number[];
                  if (
                    newTrialState.stripsData?.offsets &&
                    newTrialState.stripsData.offsets.length > 0
                  ) {
                    currentStripOffsets = newTrialState.stripsData.offsets;
                  } else {
                    // Calculate fresh default offsets based on current AB line width
                    currentStripOffsets = [
                      selectedAbLineWidth,
                      selectedAbLineWidth * 2,
                    ];
                  }
                  const temp = addStripLinesToMap(
                    mapRef,
                    abLine as any, // Type cast to handle type mismatch
                    currentStripOffsets, // Use the offsets from trial state
                    geoJsonData,
                    "demo-strip-lines",
                    {
                      lineColor: "#000000",
                      lineWidth: 2,
                      stripOpacity: 0.3,
                      stripColors: [
                        "#ff000060",
                        "#00ff0060",
                        "#0000ff60",
                        "#ffff0060",
                        "#ff00ff60",
                      ],
                      // Single source of strip number labels (1..N, repeated on
                      // every disjoint piece of a MultiPolygon strip). Shown on
                      // both the Strip Design and Rates screens.
                      showLabels: true,
                      // Only show strip controls on Plot Design step (step 1)
                      onStripLineAdjust:
                        current === 1 ? handleStripLineAdjust : undefined,
                    }
                  );

                  if (temp) {
                    if (temp.strips && temp.strips.length > 0) {
                      // Update trial state with strip area data AND geometry preservation
                      const stripDetails = temp.strips.map((strip) => ({
                        index: strip.index,
                        area: strip.area,
                        stripLength: strip.stripLength,
                        stripWidth: strip.stripWidth,
                      }));

                      const areas = temp.strips.map((strip) => strip.area);

                      // Only update state if the areas have actually changed
                      const currentAreas = newTrialState.stripsData?.areas;
                      const areasChanged =
                        !currentAreas ||
                        currentAreas.length !== areas.length ||
                        currentAreas.some(
                          (area, index) => Math.abs(area - areas[index]) > 0.01
                        );

                      if (areasChanged) {
                        // Prepare geometry updates for strip plots
                        const stripGeometryUpdates = temp.strips.map(
                          (strip) => ({
                            stripIndex: strip.index,
                            geometry: strip.geometry,
                            area: strip.area,
                            length: strip.stripLength,
                            width: strip.stripWidth,
                          })
                        );

                        // Validate geometry consistency before update
                        // Check for strip groups or individual strip plots
                        const stripPlots =
                          newTrialState.trial_plots?.filter(
                            (tp) =>
                              tp.plot.isStripPlot &&
                              typeof tp.plot.stripIndex === "number"
                          ) || [];

                        const stripGroups =
                          newTrialState.trial_plots?.filter(
                            (tp) =>
                              tp.plot.isStripPlot &&
                              (tp.plot.totalStrips ?? 0) > 0
                          ) || [];

                        const totalExpectedPlots =
                          stripPlots.length +
                          stripGroups.reduce(
                            (sum, group) => sum + (group.plot.totalStrips ?? 0),
                            0
                          );

                        setNewTrialState((prevState: any) => {
                          // Only create stripDetails if user has applied from popup
                          // Check the appliedFromPopup flag
                          const shouldCreateStripDetails =
                            prevState.stripsData?.appliedFromPopup === true;

                          // Update strip data
                          const updatedState = {
                            ...prevState,
                            stripsData: {
                              ...prevState.stripsData,
                              areas,
                              ...(shouldCreateStripDetails
                                ? { stripDetails }
                                : {}),
                            },
                          };

                          // Update trial plots with strip geometries if we have strip plots
                          if (stripGeometryUpdates.length > 0) {
                            try {
                              const updatedTrialPlots =
                                updateTrialPlotsWithStripGeometries(
                                  updatedState.trial_plots,
                                  stripGeometryUpdates
                                );

                              if (
                                updatedTrialPlots &&
                                Array.isArray(updatedTrialPlots)
                              ) {
                                // Mark all strip plots as having updated geometry from strips
                                updatedTrialPlots.forEach((tp: any) => {
                                  if (tp.plot?.isStripPlot) {
                                    tp.plot.geometrySource = "strip"; // Mark as updated from strip calculation
                                  }
                                });

                                updatedState.trial_plots = updatedTrialPlots;
                              }
                            } catch (error) {
                              console.error(
                                "😱 Error updating trial plots with strip geometries:",
                                error
                              );
                              // Continue without geometry update to prevent crashes
                            }
                          }

                          return updatedState;
                        });
                      }

                      // Add to cache after successful processing
                      stripLinesCache.add(cacheKey);
                    }
                  }
                } catch (error) {
                  console.error("❌ Error calling addStripLinesToMap:", error);
                  if (error instanceof Error) {
                    console.error("❌ Error stack:", error.stack);
                  }
                }
              } // End of else block for strip processing
            } // End of if block for processing strips when stripsData exists
          } else {
            console.error("❌ No AB line found in newTrialState.ab_line[0]");
          }
        }
        newTrialState?.trial_plots?.forEach(
          (trialItem: TrialPlot, index: number) => {
            const trialPlotId = trialItem?.plot?.id;
            const replicantIds = trialItem?.replicants?.map((rep) => rep.id);
            const rotationMarkerId = `rotation-marker-${trialPlotId as number}`;
            const rotationMarkerIdsReplicants = replicantIds?.map(
              (replicantId) =>
                `rotation-marker-replicant-${replicantId as number}`
            );
            const envelopeCenterMarkerId = `envelope-center-marker-${
              trialPlotId as number
            }`;
            const envelopeCenterMarkersIdReplicants = replicantIds?.map(
              (replicantId) =>
                `envelope-center-marker-replicant-${replicantId as number}`
            );

            const isStripMode = newTrialState.stripsData?.isStripMode === true;

            if (current !== 1 || isStripMode) {
              if (markersForTrialPlot[envelopeCenterMarkerId]) {
                markersForTrialPlot[envelopeCenterMarkerId].remove();
              }
              if (markersForTrialPlot[rotationMarkerId]) {
                markersForTrialPlot[rotationMarkerId].remove();
              }

              replicantIds?.forEach((replicantId) => {
                const envelopeCenterMarkerIdReplicant = `envelope-center-marker-replicant-${replicantId}`;
                const rotationMarkerIdReplicant = `rotation-marker-replicant-${replicantId}`;

                if (markersForTrialPlot[envelopeCenterMarkerIdReplicant]) {
                  markersForTrialPlot[envelopeCenterMarkerIdReplicant].remove();
                }

                if (markersForTrialPlot[rotationMarkerIdReplicant]) {
                  markersForTrialPlot[rotationMarkerIdReplicant].remove();
                }
              });
            }
          }
        );
      }

      if (
        map &&
        productivityZonesData?.results[0]?.predictions[0]?.productivity_zones
          .features.length > 0
      ) {
        {
          addAutoZonesLayer(
            map,
            productivityZonesData,
            autozoneOpacity,
            autozoneColorPalette
          );
          setIsAutozonesLoaded(true);
          //

          if (fieldOperationsData) {
            addFieldOperationsLayer(map, fieldOperationsData);
          }

          // --- SOIL LAYER LOGIC ---
          if (soilOperationsData) {
            addSoilImageLayer(
              map,
              {
                geoJsonData: soilOperationsData?.geoJsonData,
                zoneData: soilOperationsData?.zoneData,
              },
              soilOperationsData?.palette || "default",
              soilOperationsData?.opacity || 60
            );
          } else {
            // Remove soil layer if not present
            removeSoilImageLayer(map);
          }
        }
      }

      // Re-run the alignment check now that the AB-line/machinery-grid and
      // trial-plot layers above have actually been added to the map.
      if (current === 1) {
        checkAndReportAlignment();
      }

      if (current === 2 || current === 3) {
        const ratesDosagesArray = currentRatesDosage?.rates_and_dosages;
        if (ratesDosagesArray && ratesDosagesArray.length > 0) {
          addRatesAndDosageToMap(
            ratesDosagesArray,
            map,
            trialProducts,
            currentRatesDosage?.unit_of_rate,
            isFertilizerTabActive,
            activeRatesTab,
            bioApplicationIndex
          );
        }
      }

      // field name labels always appear on top of all other layers
      if (map && map.getLayer("polygon-name")) {
        try {
          map.moveLayer("polygon-name");
        } catch (e) {}
      }
    } catch (error) {
      console.error("Error in updateMapData:", error);
    } finally {
      isUpdatingMapData = false;
    }
  };

  return {
    createCustomMarkerElement,
    updateMapData,
    updateABWidth,
    checkPlotBoundaries,
    updateStripsData,
    clearStripLinesCache,
    clearStripLinesFromMap,
  };
};

export const initializeMap = (
  map: mapboxgl.Map | null,
  fieldData: FieldDataInterface[],
  trialInfo: TrialInfoInterface,
  newTrialState: NewTrialStateInterface,
  current: number,
  handleFieldSelection: any
): any => {
  if (!map || !fieldData || fieldData.length === 0) {
    return undefined;
  }
  const featuresWithNames = fieldData.map((item: FieldDataInterface) => {
    const isSelected = trialInfo?.field_ids?.includes(item.id);
    return {
      ...item,
      properties: {
        name: `${item.name}`,
        fieldId: item.id,
        selected: isSelected,
      },
      type: "Feature",
    };
  });

  const selectedPolygon = featuresWithNames.find(
    (item: FieldDataInterface) => item.properties?.selected
  );

  const selectedPolygons = selectedPolygon ? [selectedPolygon] : [];

  const polygonGeoJSON: any = {
    type: "FeatureCollection",
    features: current === 0 ? featuresWithNames : selectedPolygons,
  };

  const geojsonFieldDataSource = map.getSource(
    "geojson-field-data"
  ) as mapboxgl.GeoJSONSource;
  geojsonFieldDataSource?.setData(polygonGeoJSON);

  const centroidFeatures = (
    current === 0 ? featuresWithNames : selectedPolygons
  )
    .map((feature: any) => {
      try {
        const pointOnFeature = turf.pointOnFeature(feature as any);
        return {
          type: "Feature",
          geometry: pointOnFeature.geometry,
          properties: {
            name: feature.properties?.name,
            fieldId: feature.properties?.fieldId,
            selected: feature.properties?.selected,
          },
        };
      } catch (error) {
        console.error("Error calculating point on feature:", error);
        return null;
      }
    })
    .filter(Boolean);

  const centroidGeoJSON: any = {
    type: "FeatureCollection",
    features: centroidFeatures,
  };

  const geojsonFieldLabelsSource = map.getSource(
    "geojson-field-labels"
  ) as mapboxgl.GeoJSONSource;
  geojsonFieldLabelsSource?.setData(centroidGeoJSON);

  const fieldIdList = fieldData.map((item: FieldDataInterface) => item.id);

  if (current === 0) {
    map.on("click", "polygon-fill", (e: mapboxgl.MapLayerMouseEvent) => {
      const clickedFeature = e.features?.[INDEX_ZERO];
      if (!clickedFeature || !allowFieldInputChange(trialInfo, newTrialState)) {
        return;
      }
      const clickedFieldId = clickedFeature.properties?.fieldId;
      const updatedTrialInfo: TrialInfoInterface = {
        ...trialInfo,
        field_ids: fieldIdList.includes(clickedFieldId) ? [clickedFieldId] : [],
      };

      handleFieldSelection(clickedFieldId);
    });
  }

  // Ensure field name labels always appear on top of all other layers
  if (map.getLayer("polygon-name")) {
    try {
      map.moveLayer("polygon-name");
    } catch (e) {
      // Layer might not exist or can't be moved
    }
  }

  return polygonGeoJSON;
};

const addTrialPlotToMap = (
  map: mapboxgl.Map | null,
  newTrialState: NewTrialStateInterface,
  polygonGeoJSON: PolygonGeoJSON,
  createCustomMarkerElement: (icon: string) => HTMLElement,
  plotsValidationData: PlotsValidationData,
  setPlotsValidationData: any,
  checkPlotBoundaries: any
): void => {
  if (!map || !newTrialState) return;

  if (previousTrialPlots) {
    const previousIds = new Set(
      previousTrialPlots.map((trialItem) => trialItem.plot.id)
    );

    const currentIds = new Set(
      newTrialState.trial_plots.map((trialItem) => trialItem.plot.id)
    );

    previousIds.forEach((id) => {
      if (!currentIds.has(id)) {
        removeTrialPlotLayersAndSources(
          map,
          id as number,
          previousTrialPlots as any
        );
        removeMarkersForTrialPlot(id as number);
      }
    });

    previousTrialPlots.forEach((previousTrialItem: TrialPlot) => {
      const newTrialItem = newTrialState.trial_plots.find(
        (item) => item.plot.id === previousTrialItem.plot.id
      );

      // Check if the number of plots (rows × columns) has changed
      if (newTrialItem) {
        const previousPlotCount =
          previousTrialItem.plot.rows * previousTrialItem.plot.columns;
        const newPlotCount = newTrialItem.plot.rows * newTrialItem.plot.columns;

        if (previousPlotCount > newPlotCount) {
          // Remove labels for plots that no longer exist
          const trialPlotId = previousTrialItem.plot.id as number;
          for (let index = newPlotCount; index < previousPlotCount; index++) {
            const cellLayerId = `${trialPlotId}-${index}`;
            const labelSourceId = `${cellLayerId}-label`;
            const labelLayerId = `${labelSourceId}-label-plot`;

            if (map.getLayer(labelLayerId)) {
              map.removeLayer(labelLayerId);
            }
            if (map.getSource(labelSourceId)) {
              map.removeSource(labelSourceId);
            }
          }
        }
      }

      if (previousTrialItem.replicants) {
        previousTrialItem.replicants.forEach((replicant, index) => {
          const stillExists = newTrialItem?.replicants?.some(
            (newReplicant) => newReplicant.id === replicant.id
          );

          if (!stillExists) {
            removeReplicantLayersAndSources(
              map,
              previousTrialItem.plot.id as number,
              replicant.id as number,
              index
            );
          } else if (newTrialItem) {
            // Check if the number of plots has changed in the replicant
            const previousPlotCount =
              previousTrialItem.plot.rows * previousTrialItem.plot.columns;
            const newPlotCount =
              newTrialItem.plot.rows * newTrialItem.plot.columns;

            if (previousPlotCount > newPlotCount) {
              // Remove labels for replicant plots that no longer exist
              const trialPlotId = previousTrialItem.plot.id as number;
              const cloneIndex = index;
              for (
                let plotIndex = newPlotCount;
                plotIndex < previousPlotCount;
                plotIndex++
              ) {
                const cellLayerId = `cloned-${cloneIndex}-${trialPlotId}-${plotIndex}`;
                const labelSourceId = `${cellLayerId}-label`;
                const labelLayerId = `${labelSourceId}-label-plot`;

                if (map.getLayer(labelLayerId)) {
                  map.removeLayer(labelLayerId);
                }
                if (map.getSource(labelSourceId)) {
                  map.removeSource(labelSourceId);
                }
              }
            }
          }
        });
      }
    });
  }

  newTrialState.trial_plots.forEach(
    (trialItem: TrialPlot, plotIndex: number) => {
      const rows = trialItem.plot.rows;
      const columns = trialItem.plot.columns;
      const plotWidth = trialItem.plot.plotWidth;
      const plotHeight = trialItem.plot.plotHeight;
      const columnSize = trialItem.plot.column_size as number;
      const rowSize = trialItem.plot.row_size as number;
      const plotGeoJson = trialItem?.plot?.geojson;
      const trialPlotId = trialItem.plot.id as number;
      const randomPointsSourceId = `random-points-${trialPlotId}`;
      const envelopeSourceId = `trial-plot-envelope-${trialPlotId}`;
      const gridSourceId = `grid-of-trial-plots-${trialPlotId}`;
      const gridLayerId = `grid-layer-${trialPlotId}`;
      const transParentLayerId = `transparent-layer-${trialPlotId}`;

      createNewTrialPlots(
        map,
        trialPlotId,
        polygonGeoJSON,
        randomPointsSourceId,
        envelopeSourceId,
        gridSourceId,
        gridLayerId,
        rows,
        columns,
        plotWidth as number,
        plotHeight as number,
        columnSize,
        rowSize,
        plotGeoJson,
        newTrialState,
        createCustomMarkerElement,
        trialItem.plot.visible as boolean,
        plotIndex, // Use the index in the array instead of the plot ID
        transParentLayerId,
        trialItem,
        previousTrialPlots,
        plotsValidationData,
        setPlotsValidationData,
        checkPlotBoundaries
      );
    }
  );
  previousTrialPlots = [...newTrialState.trial_plots];
};

const removeTrialPlotLayersAndSources = (
  map: mapboxgl.Map,
  id: number,
  previousTrialPlots: TrialPlot[]
): void => {
  const randomPointsSourceId = `random-points-${id}`;
  const envelopeSourceId = `trial-plot-envelope-${id}`;
  const envelopeLayerId = `envelope-layer-${id}`;
  const gridSourceId = `grid-of-trial-plots-${id}`;
  const gridLayerId = `grid-layer-${id}`;
  const transParentLayerId = `transparent-layer-${id}`;

  if (map.getLayer(gridLayerId)) {
    map.removeLayer(gridLayerId);
  }

  // Remove all layers and sources related to this trial plot
  map?.getStyle()?.layers?.forEach((layer) => {
    if (layer?.id?.includes(id?.toString())) {
      map?.removeLayer(layer?.id);
    }
  });

  // Remove plot label HTML markers
  const trialItem = previousTrialPlots.find((item) => item.plot.id === id);
  if (trialItem) {
    const rows = trialItem.plot.rows;
    const columns = trialItem.plot.columns;
    const totalPlots = rows * columns;

    removePlotLabelMarkers(id, totalPlots);
  }

  // Remove group label layer and source
  const groupLabelSourceId = `${gridSourceId}-group-label`;
  const groupLabelLayerId = `${groupLabelSourceId}-label-group`;
  if (map.getLayer(groupLabelLayerId)) {
    map.removeLayer(groupLabelLayerId);
  }
  if (map.getSource(groupLabelSourceId)) {
    map.removeSource(groupLabelSourceId);
  }

  if (map.getSource(gridSourceId)) {
    map.removeSource(gridSourceId);
  }
  if (map.getLayer(envelopeLayerId)) {
    map.removeLayer(envelopeLayerId);
  }
  if (map.getSource(envelopeSourceId)) {
    map.removeSource(envelopeSourceId);
  }
  if (map.getSource(randomPointsSourceId)) {
    map.removeSource(randomPointsSourceId);
  }
  if (map.getSource(transParentLayerId)) {
    map.removeSource(transParentLayerId);
  }

  const markerId = `drag-marker-${id}`;
  if (markersForTrialPlot[markerId]) markersForTrialPlot[markerId].remove();

  const marker2Id = `rotate-marker-${id}`;
  if (markersForTrialPlot[marker2Id]) markersForTrialPlot[marker2Id].remove();

  const marker3Id = `rotation-marker-${id}`;
  if (markersForTrialPlot[marker3Id]) markersForTrialPlot[marker3Id].remove();

  if (trialItem?.replicants) {
    trialItem.replicants.forEach((replicant, index) => {
      removeReplicantLayersAndSources(
        map,
        trialItem.plot.id as number,
        replicant.id as number,
        index
      );
    });
  }
};

const removeReplicantLayersAndSources = (
  map: mapboxgl.Map,
  trialPlotId: number,
  id: number,
  index: number
): void => {
  const cloneGridSourceId = `cloned-grid-of-trial-plots-${index}-${trialPlotId}-${id}`;
  const cloneGridLayerId = `cloned-grid-layer-${index}-${trialPlotId}-${id}`;
  const envelopeCenterMarkerId = `envelope-center-marker-replicant-${id}`;
  const rotationMarkerId = `rotation-marker-replicant-${id}`;

  if (markersForTrialPlot[envelopeCenterMarkerId]) {
    markersForTrialPlot[envelopeCenterMarkerId].remove();
  }
  if (markersForTrialPlot[rotationMarkerId]) {
    markersForTrialPlot[rotationMarkerId].remove();
  }

  // Remove all layers related to this replicant
  map?.getStyle()?.layers?.forEach((layer) => {
    if (layer.id.includes(`cloned-${index}-${trialPlotId.toString()}`)) {
      map.removeLayer(layer.id);
    }
  });

  // Remove replicant group label
  const replicantGroupLabelSourceId = `${cloneGridSourceId}-group-label`;
  const replicantGroupLabelLayerId = `${replicantGroupLabelSourceId}-label-group`;
  if (map.getLayer(replicantGroupLabelLayerId)) {
    map.removeLayer(replicantGroupLabelLayerId);
  }
  if (map.getSource(replicantGroupLabelSourceId)) {
    map.removeSource(replicantGroupLabelSourceId);
  }

  if (map.getLayer(cloneGridLayerId)) {
    map.removeLayer(cloneGridLayerId);
  }
  if (map.getSource(cloneGridSourceId)) {
    map.removeSource(cloneGridSourceId);
  }
};

const removeMarkersForTrialPlot = (trialPlotId: number): void => {
  const envelopeCenterMarkerId = `envelope-center-marker-${trialPlotId}`;
  const rotationMarkerId = `rotation-marker-${trialPlotId}`;

  if (markersForTrialPlot[envelopeCenterMarkerId]) {
    markersForTrialPlot[envelopeCenterMarkerId].remove();
  }

  if (markersForTrialPlot[rotationMarkerId]) {
    markersForTrialPlot[rotationMarkerId].remove();
  }
};

const markerForTrials = (
  map: mapboxgl.Map,
  position: mapboxgl.LngLatLike,
  icon: string,
  id: number,
  createCustomMarkerElement: (icon: string) => HTMLElement
): mapboxgl.Marker => {
  const markerId = `rotate-marker-${id}`;

  const marker = new mapboxgl.Marker({
    draggable: true,
    element: createCustomMarkerElement(icon),
  })
    .setLngLat(position)
    .addTo(map);

  markersForTrialPlot[markerId] = marker;
  return marker;
};

const markerForTrialPlots = (
  map: mapboxgl.Map,
  position: mapboxgl.LngLatLike,
  icon: string,
  id: number,
  createCustomMarkerElement: (icon: string) => HTMLElement
): mapboxgl.Marker => {
  const markerId = `drag-marker-${id}`;

  const marker = new mapboxgl.Marker({
    draggable: true,
    element: createCustomMarkerElement(icon),
  })
    .setLngLat(position)
    .addTo(map);

  markersForTrialPlot[markerId] = marker;
  return marker;
};

function getRotated(
  squareGrid: GeoJSON,
  rotationAngle: number,
  rows: number,
  columns: number
): any {
  const features: any = squareGrid.features;

  const firstPolygon: any = features[0].geometry.coordinates[0];

  const lastPolygon: any =
    features[features.length - 1].geometry.coordinates[0];

  const northWest = firstPolygon[1];
  const northEast = features[columns - 1].geometry.coordinates[0][1];
  const southWest =
    features[features.length - columns].geometry.coordinates[0][3];
  const southEast = lastPolygon[0];

  const corners = [northWest, northEast, southEast, southWest];

  const center = turf.center(squareGrid).geometry.coordinates;
  const rotatedCorners = corners.map((corner) =>
    turf.transformRotate(turf.point(corner), rotationAngle, { pivot: center })
  );
  return rotatedCorners[0].geometry.coordinates;
}

function getNorthWestCorner(
  squareGrid: GeoJSON,
  rows: number,
  columns: number
): [number, number] {
  const features: any = squareGrid.features;

  const firstPolygon: any = features[0].geometry.coordinates[0];

  const lastPolygon: any =
    features[features.length - 1].geometry.coordinates[0];
  const northWest = firstPolygon[1];
  const northEast = features[columns - 1].geometry.coordinates[0][1];
  const southWest =
    features[features.length - columns].geometry.coordinates[0][3];
  const southEast = lastPolygon[0];

  // return [northWest, northEast, southEast, southWest];
  return northWest;
}

const setLayerVisibilityOfTrialPlots = (
  map: mapboxgl.Map,
  gridLayerId: string,
  visible: boolean,
  id: number,
  gridGeoJSON: any,
  gridSourceId: string,
  transParentLayerId: string
): void => {
  if (visible) {
    map.setLayoutProperty(gridLayerId, "visibility", "visible");
    map.setLayoutProperty(transParentLayerId, "visibility", "visible");
    if (gridPositions[id] && gridRotations[id]) {
      map.setCenter([gridPositions[id].lng, gridPositions[id].lat]);
      (map.getSource(gridSourceId) as mapboxgl.GeoJSONSource).setData(
        turf.transformRotate(gridGeoJSON, gridRotations[id], {
          pivot: turf.center(gridGeoJSON).geometry.coordinates,
        })
      );
    }
    if (gridPositions[id]) {
      map.setCenter([gridPositions[id].lng, gridPositions[id].lat]);
    }
  } else {
    map.setLayoutProperty(gridLayerId, "visibility", "none");
    map.setLayoutProperty(transParentLayerId, "visibility", "none");
  }

  const markerId = `drag-marker-${id}`;
  const marker2Id = `rotate-marker-${id}`;
  const marker3Id = `envelope-center-marker-${id}`;

  if (markersForTrialPlot[marker3Id]) {
    markersForTrialPlot[marker3Id].getElement().style.display = visible
      ? "block"
      : "none";
  }

  if (markersForTrialPlot[markerId]) {
    markersForTrialPlot[markerId].getElement().style.display = visible
      ? "block"
      : "none";
  }
  if (markersForTrialPlot[marker2Id]) {
    markersForTrialPlot[marker2Id].getElement().style.display = visible
      ? "block"
      : "none";
  }
};

const addLabelToMap = (
  map: mapboxgl.Map,
  sourceId: string,
  label: string,
  position: "top" | "bottom" | "left" | "right" | "center",
  offset: [number, number],
  type: string,
  size: number
) => {
  const labelLayerId = `${sourceId}-label-${type}`;

  // Set default values for different label types
  const defaultOffsets: Record<string, [number, number]> = {
    group: [0, -0.2], // Group labels (A, B, C, etc.) slightly above the plot
    plot: [0, 0], // Plot labels (1, 2, 3, etc.) centered
  };

  const defaultSizes: Record<string, number> = {
    group: 14, // Larger for group labels
    plot: 12, // Normal size for plot labels
  };

  // Use the provided values or defaults
  const finalOffset = offset || defaultOffsets[type] || [0, 0];
  const finalSize = size || defaultSizes[type] || 12;

  // Remove existing label layer if it exists
  if (map.getLayer(labelLayerId)) {
    map.removeLayer(labelLayerId);
  }

  // Add the label layer at the top of all layers
  const style = map.getStyle();
  const layers = style?.layers || [];
  const topLayerId =
    layers.length > 0 ? layers[layers.length - 1].id : undefined;

  map.addLayer(
    {
      id: labelLayerId,
      type: "symbol",
      source: sourceId,
      layout: {
        "text-field": String(label), // Ensure label is treated as a string
        "text-size": finalSize,
        "text-offset": finalOffset,
        "text-anchor": position,
        // "text-allow-overlap": true, // Allow labels to overlap with other map elements
        //  "text-ignore-placement": true, // Prioritize showing these labels
        "symbol-z-order": "source", // Ensure labels appear on top based on source order
      },
      paint: {
        "text-color": type === "group" ? "#FFFFFF" : "#FFFFFF",
        "text-halo-color": type === "group" ? "#000000" : "#000000",
        "text-halo-width": type === "group" ? 2 : 1.5, // Stronger halo for group labels
        "text-opacity": 1,
      },
    },
    topLayerId
  );
};

/**
 * Updates or creates a group label source/layer for a trial plot or replicant.
 * Shared helper to eliminate duplicate group-label-position code in drag handlers.
 */
const updateGroupLabelPosition = (
  map: mapboxgl.Map,
  baseSourceId: string,
  rotatedGrid: any,
  newTrialState: NewTrialStateInterface,
  trialPlotId: number,
  labelSuffix: string,
  offset: [number, number],
  fontSize: number
): void => {
  const bounds = turf.bbox(rotatedGrid);
  const center = turf.center(rotatedGrid);
  const labelPoint = turf.point([
    center.geometry.coordinates[0],
    bounds[3] + (bounds[3] - bounds[1]) * 0.01,
  ]);
  const groupLabelSourceId = `${baseSourceId}-group-label`;
  const groupLabelSource = map.getSource(
    groupLabelSourceId
  ) as mapboxgl.GeoJSONSource;
  if (groupLabelSource) {
    groupLabelSource.setData(labelPoint);
  } else {
    map.addSource(groupLabelSourceId, { type: "geojson", data: labelPoint });
    const groupLabelLayerId = `${groupLabelSourceId}-label-group`;
    if (!map.getLayer(groupLabelLayerId)) {
      const plotIndex = newTrialState.trial_plots.findIndex(
        (tp) => tp.plot.id === trialPlotId
      );
      const groupLabel =
        String.fromCharCode(65 + (plotIndex % 26)) + labelSuffix;
      addLabelToMap(
        map,
        groupLabelSourceId,
        groupLabel,
        "bottom",
        offset,
        "group",
        fontSize
      );
    }
  }
};

/**
 * Updates HTML marker positions for each plot cell after rotation/drag.
 */
export const updatePlotLabelMarkersPosition = (
  rotatedGrid: any,
  trialPlotId: number
): void => {
  rotatedGrid.features.forEach((feature: any, index: number) => {
    const markerId = `plot-label-marker-${trialPlotId}-${index}`;
    const marker = markersForTrialPlot[markerId];
    if (marker) {
      const center = turf.centerOfMass(feature).geometry.coordinates as [
        number,
        number
      ];
      marker.setLngLat(center);
    }
  });
};

/**
 * Removes all HTML plot-label markers for a trial plot from the map and registry.
 * Called when a trial plot grid is deleted or the trial view is exited.
 */
export const removePlotLabelMarkers = (
  trialPlotId: number,
  totalPlots: number
): void => {
  for (let index = 0; index < totalPlots; index++) {
    const markerId = `plot-label-marker-${trialPlotId}-${index}`;
    if (markersForTrialPlot[markerId]) {
      markersForTrialPlot[markerId].remove();
      delete markersForTrialPlot[markerId];
    }
  }
};

/**
 * Remove EVERY HTML marker belonging to a plot — its cell number labels (any
 * count) and its move/rotate handles — from both the map and the registry. This
 * is the single cleanup that runs before a plot is (re)rendered, so no marker can
 * survive at a stale position after a resize, drag, rotate, or any re-render
 * (whatever the previous cell count or handle state). The trailing "-" on the
 * label prefix and the exact-match handle ids avoid colliding with another plot
 * whose id shares a digit prefix (e.g. 123 vs 1234) or with replicant markers
 * (…-replicant-…), which are cleaned separately.
 */
export const removeAllMarkersForPlot = (trialPlotId: number): void => {
  const labelPrefix = `plot-label-marker-${trialPlotId}-`;
  const centerMarkerId = `envelope-center-marker-${trialPlotId}`;
  const rotationMarkerId = `rotation-marker-${trialPlotId}`;
  Object.keys(markersForTrialPlot).forEach((markerId) => {
    if (
      markerId.startsWith(labelPrefix) ||
      markerId === centerMarkerId ||
      markerId === rotationMarkerId
    ) {
      markersForTrialPlot[markerId].remove();
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete markersForTrialPlot[markerId];
    }
  });
};

/**
 * Updates or creates GeoJSON label sources/layers for each replicant plot cell.
 */
const updateReplicantPlotLabels = (
  map: mapboxgl.Map,
  rotatedGrid: any,
  cloneIndex: number,
  trialPlotId: number
): void => {
  rotatedGrid.features.forEach((feature: any, index: number) => {
    const cellLayerId = `cloned-${cloneIndex}-${trialPlotId}-${index}`;
    const labelSourceId = `${cellLayerId}-label`;
    const labelSource = map.getSource(labelSourceId) as mapboxgl.GeoJSONSource;
    if (labelSource) {
      labelSource.setData(turf.featureCollection([feature]));
    } else {
      map.addSource(labelSourceId, {
        type: "geojson",
        data: turf.featureCollection([feature]),
      });
      addLabelToMap(
        map,
        labelSourceId,
        `${index + 1}`,
        "center",
        [0, 0],
        "plot",
        12
      );
    }
  });
};

/**
 * Creates a plot label marker and adds it to the map and registry.
 * Side effects: adds marker to the map and stores it in markersForTrialPlot registry.
 */
export const createPlotLabelMarker = (
  map: mapboxgl.Map,
  lngLat: [number, number],
  label: string,
  trialPlotId: number,
  index: number
): void => {
  const el = document.createElement("div");
  el.textContent = label;
  el.style.cssText =
    "color:#fff;font-size:12px;font-weight:600;pointer-events:none;" +
    "text-shadow:-1px -1px 0 #000,1px -1px 0 #000,-1px 1px 0 #000,1px 1px 0 #000;";

  const marker = new mapboxgl.Marker({ element: el, anchor: "center" })
    .setLngLat(lngLat)
    .addTo(map);

  const markerId = `plot-label-marker-${trialPlotId}-${index}`;
  markersForTrialPlot[markerId] = marker;
};

/**
 * Build one map feature per strip for a strip group, each a Polygon or
 * MultiPolygon (mapbox fills all subparts). The geometries are recomputed from
 * the saved offsets so we ALWAYS get exactly one cell per strip — even when
 * editing a loaded trial. The saved geometry is a GeometryCollection (one cell
 * per strip), but we recompute as the primary source so it works even before the
 * backend round trip. Each feature's id matches a regular plot cell
 * (`${plotId}-0-${col}`), so the rates step colours each strip independently.
 */
const buildStripCellFeatures = (
  trialItem: any,
  trialPlotId: number,
  polygonGeoJSON: PolygonGeoJSON,
  newTrialState: NewTrialStateInterface
): any[] => {
  const makeFeature = (geometry: any, stripIndex: number) => ({
    type: "Feature" as const,
    geometry: geometry?.type === "Feature" ? geometry.geometry : geometry,
    properties: {
      id: `${trialPlotId}-0-${stripIndex}`,
      color: RGBA_PLOT_FILL,
      stripIndex,
    },
  });

  // Primary: recompute the strips from the saved offsets (deterministic).
  try {
    const offsets = newTrialState?.stripsData?.offsets;
    const abIndex = newTrialState?.stripsData?.selectedAbLineIndex ?? 0;
    const stripAbLine =
      newTrialState?.ab_line?.[abIndex] ?? newTrialState?.ab_line?.[0];
    const fieldGeometry = polygonGeoJSON?.features?.[0]?.geometry;
    if (
      Array.isArray(offsets) &&
      offsets.length > 0 &&
      (stripAbLine?.geojson?.geometry?.coordinates?.length ?? 0) >= 2 &&
      fieldGeometry
    ) {
      const lines = generateParallelLines(
        stripAbLine as any,
        offsets,
        fieldGeometry as any
      );
      const { strips } = calculateStripAreas(lines, fieldGeometry as any);
      if (strips.length > 0) {
        return strips.map((strip, i) =>
          makeFeature(strip.geometry, strip.index ?? i)
        );
      }
    }
  } catch (error) {
    console.warn("Strip cell recompute failed; using saved geometry:", error);
  }

  // Fallback: use the saved geometry directly.
  const geom = trialItem?.plot?.geojson?.geometry;

  // Preferred saved shape: a GeometryCollection with one cell (Polygon or
  // MultiPolygon) per strip — render each member as a cell.
  if (geom?.type === "GeometryCollection" && Array.isArray(geom.geometries)) {
    return geom.geometries.map((cellGeom: any, stripIndex: number) =>
      makeFeature(cellGeom, stripIndex)
    );
  }

  // Legacy saved shape: a single flat MultiPolygon (one polygon per strip).
  const geomType = geom?.type;
  if (geomType !== "Polygon" && geomType !== "MultiPolygon") return [];
  const polygons: any[] =
    geomType === "Polygon" ? [geom.coordinates] : geom.coordinates;
  return polygons.map((polygonCoords: any, stripIndex: number) =>
    makeFeature({ type: "Polygon", coordinates: polygonCoords }, stripIndex)
  );
};

const createNewTrialPlots = (
  map: mapboxgl.Map,
  trialPlotId: number,
  polygonGeoJSON: PolygonGeoJSON,
  randomPointsSourceId: string,
  envelopeSourceId: string,
  gridSourceId: string,
  gridLayerId: string,
  rows: number,
  columns: number,
  plotWidth: number,
  plotHeight: number,
  columnSize: number,
  rowSize: number,
  plotGeoJson: any,
  newTrialState: NewTrialStateInterface,
  createCustomMarkerElement: (icon: string) => HTMLElement,
  visible: boolean,
  id: number,
  transParentLayerId: string,
  trialItem: any,
  previousTrialPlots: any,
  plotsValidationData: PlotsValidationData,
  setPlotsValidationData: any,
  checkPlotBoundaries: any
): void => {
  const envelopeCenterMarkerId = `envelope-center-marker-${trialPlotId}`;
  const rotationMarkerId = `rotation-marker-${trialPlotId}`;
  let rotationAngle = gridRotations[trialPlotId] || 0;

  // Clear ALL of this plot's markers (cell labels + move/rotate handles) up front
  // so none survive at a stale position after a resize, drag, rotate or any other
  // re-render — regardless of the previous cell count or which handles existed.
  removeAllMarkersForPlot(trialPlotId);

  // Handle strip plots differently. Render ONE cell per strip (each a Polygon or
  // MultiPolygon — mapbox fills all subparts) with the SAME per-cell fill/border
  // layers as a regular plot, so the rates step colours each strip independently
  // and every disjoint subpart of a strip fills. The per-strip geometries are
  // recomputed from the saved offsets, so we always get exactly N strips (not one
  // cell per flattened polygon). Strip plots are never moved/rotated → ALWAYS
  // return here (no drag markers).
  if (trialItem?.plot?.isStripPlot) {
    try {
      // A strip plot must NEVER show the regular plot's per-sub-polygon labels
      // (it creates one "1,2,3,..." HTML marker per polygon, so a 3-strip group
      // flattened to 5 polygons shows 1..5). Remove any that linger from a prior
      // render — strip numbers come solely from drawStripLinesToMap (per strip,
      // repeated on each disjoint piece).
      const staleLabelCount = Math.max(
        50,
        Array.isArray(plotGeoJson?.geometry?.coordinates)
          ? plotGeoJson.geometry.coordinates.length
          : 0
      );
      for (let i = 0; i < staleLabelCount; i++) {
        const staleMarkerId = `plot-label-marker-${trialPlotId}-${i}`;
        if (markersForTrialPlot[staleMarkerId]) {
          markersForTrialPlot[staleMarkerId].remove();
          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
          delete markersForTrialPlot[staleMarkerId];
        }
      }

      const stripFeatures = buildStripCellFeatures(
        trialItem,
        trialPlotId,
        polygonGeoJSON,
        newTrialState
      );

      if (stripFeatures.length > 0) {
        const stripGeoJSON = turf.featureCollection(stripFeatures);

        if (!map.getSource(gridSourceId)) {
          map.addSource(gridSourceId, {
            type: "geojson",
            data: stripGeoJSON as GeoJSON.FeatureCollection,
          });
        } else {
          (map.getSource(gridSourceId) as mapboxgl.GeoJSONSource).setData(
            stripGeoJSON as GeoJSON.FeatureCollection
          );
        }

        // Insert the rate-coloured cell fills BELOW the strip number labels
        // (drawn by drawStripLinesToMap) so the labels always stay on top. This
        // loop runs after the strip lines are drawn, so without a beforeId the
        // fills would cover the "1,2,3" labels. If no label layer is on the map
        // yet, undefined keeps the previous "add on top" behaviour.
        const stripLabelBeforeId = (map.getStyle()?.layers ?? [])
          .map((styleLayer) => styleLayer.id)
          .find((styleLayerId) => styleLayerId.endsWith("-strip-area-labels"));
        const labelBeforeId =
          stripLabelBeforeId && map.getLayer(stripLabelBeforeId)
            ? stripLabelBeforeId
            : undefined;

        // Per-strip fill + border layers, mirroring the regular-plot cells.
        stripFeatures.forEach((feature, stripIndex) => {
          const cellLayerId = `${trialPlotId}-${stripIndex}`;
          if (map.getLayer(cellLayerId)) map.removeLayer(cellLayerId);
          map.addLayer(
            {
              id: cellLayerId,
              type: "fill",
              source: gridSourceId,
              paint: {
                "fill-color": ["get", "color"],
                "fill-opacity": 1,
              },
              layout: { visibility: visible ? "visible" : "none" },
              filter: ["==", ["get", "id"], feature.properties.id],
            },
            labelBeforeId
          );

          const cellBorderLayerId = `${cellLayerId}-border`;
          if (map.getLayer(cellBorderLayerId)) {
            map.removeLayer(cellBorderLayerId);
          }
          map.addLayer(
            {
              id: cellBorderLayerId,
              type: "line",
              source: gridSourceId,
              paint: { "line-color": "#000000", "line-width": 1 },
              layout: { visibility: visible ? "visible" : "none" },
              filter: ["==", ["get", "id"], feature.properties.id],
            },
            labelBeforeId
          );
        });
      }

      // Strip plots are exempt from field-boundary validation and are never
      // draggable — always return so no move/rotate markers are created.
      // NOTE: strip number labels are rendered once by drawStripLinesToMap
      // (showLabels), so both the Strip Design and Rates screens show the same
      // labels — we do not create separate labels here.
      return;
    } catch (error) {
      console.error("❌ Error creating strip plot:", error);
      return; // Never fall through to the draggable-plot path for strip plots.
    }
  }

  let pointsWithinPolygon;

  const containsZeroCoordinates =
    trialItem?.plot?.geojson?.geometry?.coordinates &&
    Array.isArray(trialItem.plot.geojson.geometry.coordinates) &&
    trialItem.plot.geojson.geometry.coordinates.some(
      (coord: any) => coord && coord[INDEX_ZERO] === 0 && coord[INDEX_ONE] === 0
    );

  // A new plot block must fit inside a single parcel. For a MultiPolygon field,
  // seed the random placement points into the largest sub-polygon only, so the
  // block doesn't land in the gap between parcels. A plain Polygon field keeps the
  // exact previous behaviour (placementArea === polygonGeoJSON).
  const fieldPolygons = getSubPolygons(
    polygonGeoJSON.features?.[0]?.geometry as turf.Geometry
  );
  const placementArea: any =
    fieldPolygons.length > 1
      ? fieldPolygons.reduce((best, current) =>
          turf.area(current) > turf.area(best) ? current : best
        )
      : polygonGeoJSON;

  if (containsZeroCoordinates || !randomPointsCache[trialPlotId]) {
    const randomPoints = turf.randomPoint(10, {
      bbox: turf.bbox(placementArea),
    });
    pointsWithinPolygon = turf.pointsWithinPolygon(randomPoints, placementArea);
    randomPointsCache[trialPlotId] = pointsWithinPolygon;
  } else {
    pointsWithinPolygon = randomPointsCache[trialPlotId];
  }

  // Sliver / concave parcels can leave `pointsWithinPolygon` empty (all random
  // bbox points miss the polygon). `turf.envelope` of an empty collection is
  // degenerate, which would place the grid off the field. Fall back to a
  // guaranteed-interior point so the starting point is always meaningful.
  if (!pointsWithinPolygon?.features?.length) {
    pointsWithinPolygon = turf.featureCollection([
      turf.pointOnFeature(placementArea),
    ]);
    randomPointsCache[trialPlotId] = pointsWithinPolygon;
  }
  const envelope = turf.envelope(pointsWithinPolygon);

  if (!map.getSource(randomPointsSourceId)) {
    map.addSource(randomPointsSourceId, {
      type: "geojson",
      data: pointsWithinPolygon as GeoJSON.FeatureCollection,
    });
  } else {
    (map.getSource(randomPointsSourceId) as mapboxgl.GeoJSONSource).setData(
      pointsWithinPolygon as GeoJSON.FeatureCollection
    );
  }

  if (!map.getSource(envelopeSourceId)) {
    map.addSource(envelopeSourceId, {
      type: "geojson",
      data: envelope,
    });
  } else {
    (map.getSource(envelopeSourceId) as mapboxgl.GeoJSONSource).setData(
      envelope
    );
  }

  const envelopeBBox = turf.bbox(envelope);

  let gridFeatures: Array<turf.Feature<turf.Polygon>> = [];
  let gridGeoJSON = turf.featureCollection(gridFeatures);
  let initialRotationMarkerPosition: number[] = [];

  if (
    plotGeoJson?.geometry?.coordinates?.[0] === 0 ||
    plotGeoJson?.geometry?.coordinates?.[1] === 0 ||
    trialItem.plot.isEditing
  ) {
    const width = columnSize ?? plotWidth;
    const height = rowSize ?? plotHeight;

    // AB-line info: new plots are rotated to the AB bearing and snapped to the
    // machinery grid so they tile with it.
    const firstAbGrid = newTrialState.ab_line?.[0];
    let abBearing: number | null = null;
    let abFirstCoord: [number, number] | null = null;
    let abGridWidth = 50;
    if (
      newTrialState.ab_line.length > 0 &&
      Array.isArray(firstAbGrid?.geojson?.geometry?.coordinates) &&
      firstAbGrid.geojson.geometry.coordinates.length >= 2 &&
      firstAbGrid.geojson.geometry.coordinates[0] &&
      firstAbGrid.geojson.geometry.coordinates[1]
    ) {
      try {
        const abCoords = firstAbGrid.geojson.geometry.coordinates;
        abBearing = turf.bearing(
          turf.point(abCoords[0]),
          turf.point(abCoords[1])
        );
        abFirstCoord = abCoords[0] as [number, number];
        abGridWidth = firstAbGrid.grid_width ?? 50;
      } catch {
        abBearing = null;
      }
    }

    // Build a plot block centred on `centrePoint`, rotated to the AB bearing and
    // snapped to the machinery grid. `snapMode` "abOnly" pins the leading edge to
    // the AB line (used for the very first plot); "grid" snaps to the nearest
    // grid line so later plots can occupy any free row of the machinery grid.
    const buildPlacedGridAt = (
      centrePoint: [number, number],
      // "none" rotates to the AB bearing but does NOT snap to a grid line, so the
      // block stays centred exactly on `centrePoint` (used when editing keeps the
      // plot where it is — snapping would shift a resized grid off its centre).
      snapMode: "abOnly" | "grid" | "none"
    ): turf.FeatureCollection<turf.Polygon> => {
      // Offset the corner so the block is centred on `centrePoint` (the grid
      // grows east/south from its corner).
      const corner = getRhumbOffsetPoint(
        centrePoint,
        -(columns * width) / 2,
        (rows * height) / 2
      );
      const axisAlignedGrid = buildAxisAlignedPlotGrid(
        corner,
        rows,
        columns,
        width,
        height
      );
      let placed = turf.featureCollection(
        axisAlignedGrid.features.map((feature, index) => {
          const rowIndex = Math.floor(index / columns);
          const columnIndex = index % columns;
          return {
            ...feature,
            properties: {
              id: `${trialPlotId}-${rowIndex}-${columnIndex}`,
              color: RGBA_PLOT_FILL,
            },
          };
        })
      ) as turf.FeatureCollection<turf.Polygon>;

      if (abBearing !== null && abFirstCoord) {
        const centre = turf.center(placed).geometry.coordinates;
        placed = turf.transformRotate(placed, abBearing, {
          pivot: centre,
        }) as turf.FeatureCollection<turf.Polygon>;
        if (snapMode !== "none") {
          try {
            placed = snapGridToAbLine(
              placed,
              abFirstCoord,
              abBearing,
              abGridWidth,
              { mode: snapMode === "abOnly" ? "abOnly" : "nearestGrid" }
            );
          } catch (snapError) {
            console.warn(
              "⚠️ Grid-line snapping failed, using unsnapped position:",
              snapError
            );
          }
        }
      }
      return placed;
    };

    // Footprints of already-placed plots so the new block doesn't land on top of
    // an existing one.
    const existingFootprints = collectExistingPlotFootprints(
      newTrialState,
      trialPlotId
    );
    const isFirstPlot = existingFootprints.length === 0;

    // Fallback centre = the previous envelope-bbox centre (kept for parity).
    const [eMinX, eMinY, eMaxX, eMaxY] = envelopeBBox;
    const fallbackCentre: [number, number] = [
      (eMinX + eMaxX) / 2,
      (eMinY + eMaxY) / 2,
    ];

    // Candidate centres spread across the parcel (variety so plots don't stack)
    // plus the fallback centre.
    const candidateCentres: Array<[number, number]> = [
      ...generatePlacementCandidatePoints(placementArea, 60),
      fallbackCentre,
    ];

    let chosenGrid: turf.FeatureCollection<turf.Polygon> | null = null;
    let bestEffortGrid: turf.FeatureCollection<turf.Polygon> | null = null;
    let bestScore = Number.NEGATIVE_INFINITY;

    const primarySnap: "abOnly" | "grid" = isFirstPlot ? "abOnly" : "grid";

    // EDITING: keep the plot exactly where it is by pinning its ORIGIN corner —
    // the NW corner it was drawn from (where the rotate handle sits) — to its
    // current grid-aligned position and growing/shrinking with the new dimensions
    // in the plot's own direction. The origin is already on the grid, so we do NOT
    // re-snap (re-snapping is what made the whole plot drift). If the resized grid
    // then spills out of the field, pin each of the OTHER corners instead (grow
    // the other way); only if none of those fit do we slide it the minimum needed
    // toward the field centre. Overlap with other plots is tolerated here.
    if (trialItem.plot.isEditing) {
      let editCentre: [number, number] | null = null;
      let originCorner: [number, number] | null = null;
      const oldVertices: number[][] = [];
      try {
        const oldCoords = plotGeoJson?.geometry?.coordinates;
        if (Array.isArray(oldCoords) && Array.isArray(oldCoords[0]?.[0])) {
          const cells = (oldCoords as any[])
            .map((polyCoords) => {
              try {
                return turf.polygon(polyCoords);
              } catch {
                return null;
              }
            })
            .filter(
              (cell): cell is turf.Feature<turf.Polygon> => cell !== null
            );
          if (cells.length > 0) {
            editCentre = turf.center(turf.featureCollection(cells)).geometry
              .coordinates as [number, number];
            cells.forEach((cell) =>
              turf.coordAll(cell).forEach((coord) => oldVertices.push(coord))
            );
            // Origin (NW) corner: the first cell's NW vertex — the same reference
            // getNorthWestCorner / the rotate handle uses.
            originCorner = oldCoords[0]?.[0]?.[1] as [number, number];
          }
        }
      } catch {
        editCentre = null;
      }

      let fieldCentre: [number, number] = editCentre ?? fallbackCentre;
      try {
        fieldCentre = turf.centroid(turf.featureCollection(fieldPolygons))
          .geometry.coordinates as [number, number];
      } catch {
        /* keep editCentre as the slide target */
      }

      const fitsField = (grid: turf.FeatureCollection<turf.Polygon>): boolean =>
        isGridInsideSingleParcel(grid, fieldPolygons);

      if (editCentre && abBearing !== null && originCorner) {
        const bearing = abBearing;

        // Resized grid at the current centre, rotated to the AB bearing but NOT
        // snapped, then translated so its NW corner lands exactly on the plot's
        // current NW origin — so the origin stays put and grid-aligned.
        const resized = buildPlacedGridAt(editCentre, "none");
        const resizedNw = getNorthWestCorner(resized, rows, columns) as [
          number,
          number
        ];
        const alignKm = turf.distance(
          turf.point(resizedNw),
          turf.point(originCorner),
          { units: "kilometers" }
        );
        const nwAnchored =
          alignKm > 1e-9
            ? (turf.transformTranslate(
                resized,
                alignKm,
                turf.bearing(turf.point(resizedNw), turf.point(originCorner)),
                { units: "kilometers" }
              ) as turf.FeatureCollection<turf.Polygon>)
            : resized;

        // Footprint size change along the AB axes (metres): u = along the AB line,
        // v = perpendicular. Used to pin the other corners by translating back.
        const halfExtents = (
          vertices: number[][]
        ): { uHalf: number; vHalf: number } => {
          let uMin = Infinity;
          let uMax = -Infinity;
          let vMin = Infinity;
          let vMax = -Infinity;
          const origin = turf.point(editCentre as [number, number]);
          vertices.forEach((vertex) => {
            const point = turf.point(vertex);
            const dist = turf.distance(origin, point, { units: "meters" });
            const theta =
              ((turf.bearing(origin, point) - bearing) * Math.PI) / 180;
            const u = dist * Math.cos(theta);
            const v = dist * Math.sin(theta);
            if (u < uMin) uMin = u;
            if (u > uMax) uMax = u;
            if (v < vMin) vMin = v;
            if (v > vMax) vMax = v;
          });
          return { uHalf: (uMax - uMin) / 2, vHalf: (vMax - vMin) / 2 };
        };
        const oldExt = halfExtents(oldVertices);
        const newExt = halfExtents(turf.coordAll(nwAnchored));
        const du = (newExt.uHalf - oldExt.uHalf) * 2; // width change along u
        const dv = (newExt.vHalf - oldExt.vHalf) * 2; // height change along v

        const translateGrid = (
          grid: turf.FeatureCollection<turf.Polygon>,
          uM: number,
          vM: number
        ): turf.FeatureCollection<turf.Polygon> => {
          let out = grid;
          if (Math.abs(uM) > 1e-6) {
            out = turf.transformTranslate(
              out,
              Math.abs(uM) / 1000,
              uM > 0 ? bearing : bearing + 180,
              { units: "kilometers" }
            ) as turf.FeatureCollection<turf.Polygon>;
          }
          if (Math.abs(vM) > 1e-6) {
            out = turf.transformTranslate(
              out,
              Math.abs(vM) / 1000,
              vM > 0 ? bearing + 90 : bearing - 90,
              { units: "kilometers" }
            ) as turf.FeatureCollection<turf.Polygon>;
          }
          return out;
        };

        // Natural direction (NW origin fixed) first; if it spills, pin each of the
        // other corners by translating the NW-anchored grid back by the size change.
        const candidates: Array<turf.FeatureCollection<turf.Polygon>> = [
          nwAnchored,
          translateGrid(nwAnchored, -du, 0), // keep NE
          translateGrid(nwAnchored, 0, -dv), // keep SW
          translateGrid(nwAnchored, -du, -dv), // keep SE
        ];
        for (const candidate of candidates) {
          if (fitsField(candidate)) {
            chosenGrid = candidate;
            break;
          }
        }

        // Last resort: slide the NW-anchored grid toward the field centre.
        if (!chosenGrid) {
          const toFieldKm = turf.distance(
            turf.point(editCentre),
            turf.point(fieldCentre),
            { units: "kilometers" }
          );
          if (toFieldKm > 1e-9) {
            const dirBearing = turf.bearing(
              turf.point(editCentre),
              turf.point(fieldCentre)
            );
            for (let t = 0.1; t <= 1.00001 && !chosenGrid; t += 0.1) {
              const candidate = turf.transformTranslate(
                nwAnchored,
                toFieldKm * t,
                dirBearing,
                { units: "kilometers" }
              ) as turf.FeatureCollection<turf.Polygon>;
              if (fitsField(candidate)) {
                chosenGrid = candidate;
              }
            }
          }
        }
      } else if (editCentre) {
        // No AB line (no machinery grid): keep the plot where it is, unsnapped,
        // then slide toward the field centre if it doesn't fit.
        try {
          const candidate = buildPlacedGridAt(editCentre, "none");
          if (fitsField(candidate)) {
            chosenGrid = candidate;
          }
        } catch {
          /* fall through to the slide */
        }
        for (let t = 0.1; t <= 1.00001 && !chosenGrid && editCentre; t += 0.1) {
          const centre: [number, number] = [
            editCentre[0] + (fieldCentre[0] - editCentre[0]) * t,
            editCentre[1] + (fieldCentre[1] - editCentre[1]) * t,
          ];
          try {
            const candidate = buildPlacedGridAt(centre, "none");
            if (fitsField(candidate)) {
              chosenGrid = candidate;
            }
          } catch {
            /* try the next shift */
          }
        }
      }
    }

    // Search for a placement that is fully inside a single parcel AND does not
    // overlap any existing plot. Track a best-effort candidate as a fallback.
    // (Skipped when editing already kept/relocated the plot above.)
    if (!chosenGrid) {
      for (const centre of candidateCentres) {
        let candidate: turf.FeatureCollection<turf.Polygon>;
        try {
          candidate = buildPlacedGridAt(centre, primarySnap);
        } catch {
          continue;
        }
        const inside = isGridInsideSingleParcel(candidate, fieldPolygons);
        const overlaps = gridOverlapsFootprints(candidate, existingFootprints);
        if (inside && !overlaps) {
          chosenGrid = candidate;
          break;
        }
        const score =
          countCellsInsideField(candidate, fieldPolygons) -
          (overlaps ? candidate.features.length : 0);
        if (score > bestScore) {
          bestScore = score;
          bestEffortGrid = candidate;
        }
      }
    }

    // First plot: if pinning to the AB line left it outside the field, retry
    // allowing any grid line so it can sit fully inside.
    if (!chosenGrid && isFirstPlot) {
      for (const centre of candidateCentres) {
        let candidate: turf.FeatureCollection<turf.Polygon>;
        try {
          candidate = buildPlacedGridAt(centre, "grid");
        } catch {
          continue;
        }
        if (isGridInsideSingleParcel(candidate, fieldPolygons)) {
          chosenGrid = candidate;
          break;
        }
      }
    }

    gridGeoJSON =
      chosenGrid ??
      bestEffortGrid ??
      buildPlacedGridAt(fallbackCentre, primarySnap);

    initialRotationMarkerPosition = getNorthWestCorner(
      gridGeoJSON,
      rows,
      columns
    );
    trialItem.plot.geojson.geometry.coordinates = gridGeoJSON?.features.map(
      (feature) => feature?.geometry?.coordinates
    );
  } else {
    let propertiesArray: any[] = [];

    for (let i = INDEX_ZERO; i < rows; i++) {
      for (let j = INDEX_ZERO; j < columns; j++) {
        const coordinates = plotGeoJson.geometry.coordinates;

        const color = RGBA_PLOT_FILL;
        const properties = { id: `${trialPlotId}-${i}-${j}`, color };
        propertiesArray.push(properties);
      }
    }

    plotGeoJson?.geometry?.coordinates?.forEach(
      (coordinateSet: any, index: number) => {
        const feature: any = {
          type: "Feature",
          geometry: {
            type: "Polygon",
            coordinates: coordinateSet,
          },
          properties: propertiesArray[index] || {},
        };
        gridFeatures.push(feature);
      }
    );

    // Calculate bearing safely based on geometry type
    let bearing = 0;
    try {
      if (
        plotGeoJson.geometry.type === "Polygon" &&
        plotGeoJson.geometry.coordinates[0]?.length >= 2
      ) {
        // For polygon geometry (strip plots), use first two points of the outer ring
        const coords = plotGeoJson.geometry.coordinates[0];
        bearing = turf.bearing(turf.point(coords[0]), turf.point(coords[1]));
      } else if (plotGeoJson.geometry.coordinates?.length >= 2) {
        // For other geometry types, use the original logic
        bearing = turf.bearing(
          turf.point(plotGeoJson.geometry.coordinates[0][0][0]),
          turf.point(plotGeoJson.geometry.coordinates[0][0][1])
        );
      }
    } catch (error) {
      console.warn("⚠️ Error calculating bearing, using default 0:", error);
      bearing = 0;
    }

    gridGeoJSON = turf.featureCollection(gridFeatures);

    initialRotationMarkerPosition = getNorthWestCorner(
      gridGeoJSON,
      rows,
      columns
    );

    trialItem.plot.geojson.geometry.coordinates = gridGeoJSON?.features.map(
      (feature) => feature?.geometry?.coordinates
    );
  }

  let envelopeCenter: [number, number];

  try {
    envelopeCenter = turf.center(gridGeoJSON).geometry.coordinates as [
      number,
      number
    ];
  } catch (error) {
    console.warn("⚠️ Error calculating envelope center, using default:", error);
    envelopeCenter = [0, 0]; // Default fallback coordinates
  }

  const updateRotationMarkerPosition = (data?: any): void => {
    initialRotationMarkerPosition = getRotated(
      gridGeoJSON,
      rotationAngle,
      rows,
      columns
    );

    rotationMarker.setLngLat(initialRotationMarkerPosition as [number, number]);
  };

  const envelopeCenterMarker = markerForTrialPlots(
    map,
    envelopeCenter as mapboxgl.LngLatLike,
    moveIcon,
    trialPlotId,
    createCustomMarkerElement
  );

  const rotationMarker = markerForTrialPlots(
    map,
    initialRotationMarkerPosition as mapboxgl.LngLatLike,
    rotateImage,
    trialPlotId,
    createCustomMarkerElement
  );

  markersForTrialPlot[rotationMarkerId] = rotationMarker;

  rotationMarker.on("drag", () => {
    const newCenter = envelopeCenterMarker.getLngLat()?.toArray() as
      | [number, number]
      | undefined;
    const newRotationMarkerPosition = rotationMarker.getLngLat()?.toArray() as
      | [number, number]
      | undefined;

    if (newCenter && newRotationMarkerPosition) {
      const newAngle = turf.bearing(
        turf.point(newCenter),
        turf.point(newRotationMarkerPosition)
      );
      rotationAngle = newAngle;

      const rotatedGrid = turf.transformRotate(gridGeoJSON, newAngle, {
        pivot: newCenter,
      }) as GeoJSON.FeatureCollection<
        turf.helpers.Polygon,
        turf.helpers.Properties
      >;

      const source = map.getSource(gridSourceId) as
        | mapboxgl.GeoJSONSource
        | undefined;
      if (source) {
        source.setData(rotatedGrid);
      }

      // grid rotations code removed
      updateRotationMarkerPosition(rotatedGrid);

      // Update the group label position and plot label markers when rotating
      updateGroupLabelPosition(
        map,
        gridSourceId,
        rotatedGrid,
        newTrialState,
        trialPlotId,
        "",
        [0, -0.2],
        14
      );
      updatePlotLabelMarkersPosition(rotatedGrid, trialPlotId);

      trialItem.plot.geojson.geometry.coordinates = rotatedGrid?.features.map(
        (feature) => feature?.geometry?.coordinates
      );
    }
  });
  rotationMarker.on("dragend", () => {
    // Perform actions after drag is complete, like updating coordinates
    checkPlotBoundaries(
      polygonGeoJSON,
      plotsValidationData,
      setPlotsValidationData,
      newTrialState
    );
    // Report alignment status after user finishes rotating
    checkAndReportAlignment();
  });

  markersForTrialPlot[envelopeCenterMarkerId] = envelopeCenterMarker;
  envelopeCenterMarker.on("drag", () => {
    const newCenter = envelopeCenterMarker.getLngLat().toArray();
    const offset = [
      newCenter[INDEX_ZERO] - envelopeCenter[INDEX_ZERO],
      newCenter[INDEX_ONE] - envelopeCenter[INDEX_ONE],
    ];

    const newGridFeatures = gridGeoJSON.features.map((feature) => {
      const coords = feature.geometry.coordinates[INDEX_ZERO].map((point) => [
        point[INDEX_ZERO] + offset[INDEX_ZERO],
        point[INDEX_ONE] + offset[INDEX_ONE],
      ]);
      return turf.polygon([coords], feature.properties);
    });

    gridGeoJSON = turf.featureCollection(newGridFeatures);

    const rotatedGrid = turf.transformRotate(gridGeoJSON, rotationAngle, {
      pivot: newCenter,
    });

    // Update main grid source
    (map.getSource(gridSourceId) as mapboxgl.GeoJSONSource).setData(
      rotatedGrid
    );

    const newEnvelope = turf.transformTranslate(
      envelope,
      offset[INDEX_ZERO],
      offset[INDEX_ONE]
    );
    (map.getSource(envelopeSourceId) as mapboxgl.GeoJSONSource).setData(
      newEnvelope
    );

    initialRotationMarkerPosition = getNorthWestCorner(
      gridGeoJSON,
      rows,
      columns
    );
    rotationMarker.setLngLat(initialRotationMarkerPosition as [number, number]);
    updateRotationMarkerPosition(rotatedGrid);

    // Update the group label position and plot label markers
    updateGroupLabelPosition(
      map,
      gridSourceId,
      rotatedGrid,
      newTrialState,
      trialPlotId,
      "",
      [0, -0.2],
      14
    );
    updatePlotLabelMarkersPosition(rotatedGrid, trialPlotId);

    gridPositions[trialPlotId] = {
      lng: newCenter[INDEX_ZERO],
      lat: newCenter[INDEX_ONE],
    };
    envelopeCenter = newCenter as [number, number];
    trialItem.plot.geojson.geometry.coordinates = rotatedGrid.features.map(
      (feature) => feature.geometry.coordinates
    );
  });
  envelopeCenterMarker.on("dragend", () => {
    // Perform actions after drag is complete, like updating coordinates
    checkPlotBoundaries(
      polygonGeoJSON,
      plotsValidationData,
      setPlotsValidationData,
      newTrialState
    );
    checkAndReportAlignment();
  });

  // Skip validation for strip plots - they should never show red borders
  const isStripPlot = trialItem?.plot?.isStripPlot;
  const isError = isStripPlot
    ? false
    : plotsValidationData?.polygonsNotInsideField?.some(
        (item: any) => item === trialPlotId
      ) ||
      plotsValidationData.intersectingPairs?.some((item: any) =>
        item.some((plotId: any) => plotId === trialPlotId)
      );

  if (!map.getSource(gridSourceId)) {
    map.addSource(gridSourceId, {
      type: "geojson",
      data: gridGeoJSON,
    });

    map.addLayer({
      id: gridLayerId,
      type: "fill",
      source: gridSourceId,
      paint: {
        "fill-color": RGBA_PLOT_FILL,
      },
    });

    map.addLayer({
      id: transParentLayerId,
      type: "line",
      source: gridSourceId,
      paint: {
        "line-color": RGBA_TRIAL_PLOT,
        "line-width": 2,
      },
    });
  } else {
    (map.getSource(gridSourceId) as mapboxgl.GeoJSONSource).setData(
      gridGeoJSON
    );
  }

  setLayerVisibilityOfTrialPlots(
    map,
    gridLayerId,
    visible,
    id,
    gridGeoJSON,
    gridSourceId,
    transParentLayerId
  );

  gridGeoJSON.features.forEach((feature, index) => {
    const cellLayerId = `${trialPlotId}-${index}`;

    if (map.getLayer(cellLayerId)) {
      map.removeLayer(cellLayerId);
    }

    map.addLayer({
      id: cellLayerId,
      type: "fill",
      source: gridSourceId,
      paint: {
        "fill-color": ["get", "color"],
        "fill-opacity": 1,
      },
      filter: ["==", ["get", "id"], feature.properties?.id],
    });

    const cellBorderLayerId = `${cellLayerId}-border`;
    if (map.getLayer(cellBorderLayerId)) {
      map.removeLayer(cellBorderLayerId);
    }

    map.addLayer({
      id: cellBorderLayerId,
      type: "line",
      source: gridSourceId,
      paint: {
        // "line-color":  RGBA_TRANSPARENT_COLOR,
        "line-color": isError ? RGBA_TRIAL_PLOT_RED : RGBA_TRANSPARENT_COLOR,
        "line-width": 2,
      },
      filter: ["==", ["get", "id"], feature.properties?.id],
    });
  });

  // Remove ALL existing label markers for this plot first. When the grid shrinks
  // (e.g. 3×3 → 2×2) the loop below only re-adds the remaining cells, so markers
  // for the removed cells (labels 5..9) would otherwise linger at their old spots
  // (they are HTML markers positioned by lng/lat, so replacing the source data
  // does not clear them).
  Object.keys(markersForTrialPlot)
    .filter((markerId) =>
      markerId.startsWith(`plot-label-marker-${trialPlotId}-`)
    )
    .forEach((markerId) => {
      markersForTrialPlot[markerId].remove();
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete markersForTrialPlot[markerId];
    });

  // Add one HTML marker label per plot cell, centred on the cell.
  gridGeoJSON.features.forEach((feature, index) => {
    const center = turf.centerOfMass(feature).geometry.coordinates as [
      number,
      number
    ];
    createPlotLabelMarker(map, center, `${index + 1}`, trialPlotId, index);
  });

  // Add group label - must be added after all other layers
  // Convert id to A, B, C, ... (ASCII 65 is 'A')
  const groupLabel = String.fromCharCode(65 + (id % 26));
  const groupLabelSourceId = `${gridSourceId}-group-label`;
  const groupLabelLayerId = `${groupLabelSourceId}-label-group`;

  // Remove existing group label if it exists
  if (map.getLayer(groupLabelLayerId)) {
    map.removeLayer(groupLabelLayerId);
  }
  if (map.getSource(groupLabelSourceId)) {
    map.removeSource(groupLabelSourceId);
  }

  // Get the bounds of the grid to position the label outside
  const gridBounds = turf.bbox(gridGeoJSON);
  const gridCenter = turf.center(gridGeoJSON);

  // Create a point slightly above the grid for the label (with minimal offset)
  const labelPoint = turf.point([
    gridCenter.geometry.coordinates[0],
    gridBounds[3] + (gridBounds[3] - gridBounds[1]) * 0.01, // Very small offset above the grid
  ]);

  map.addSource(groupLabelSourceId, {
    type: "geojson",
    data: labelPoint,
  });

  // Add group label layer
  addLabelToMap(
    map,
    groupLabelSourceId,
    groupLabel, // Group label (A, B, C, ...)
    "bottom", // Anchor at the bottom of the text so it points to the plot
    [0, -0.2], // Small negative y offset to position closer to the plot
    "group",
    14 // Increased size for better visibility
  );

  const trialPlot = newTrialState.trial_plots.find(
    (tp) => tp.plot.id === trialPlotId
  );

  if (trialPlot?.replicants) {
    trialPlot.replicants.forEach(
      (replicants: Replicants, cloneIndex: number) => {
        addReplicantToMap(
          replicants,
          cloneIndex,
          gridGeoJSON,
          trialPlotId,
          map,
          createCustomMarkerElement,
          replicants.id as number,
          trialItem,
          plotWidth,
          plotHeight,
          rows,
          columns,
          plotsValidationData,
          setPlotsValidationData,
          checkPlotBoundaries,
          polygonGeoJSON,
          newTrialState
        );
      }
    );
  }
};

const addReplicantToMap = (
  replicants: Replicants,
  cloneIndex: number,
  gridGeoJSON: GeoJSON,
  trialPlotId: number,
  map: mapboxgl.Map,
  createCustomMarkerElement: (icon: string) => HTMLElement,
  replicantId: number,
  trialItem: any,
  plotWidth: any,
  plotHeight: any,
  rows: number,
  columns: number,
  plotsValidationData: any,
  setPlotsValidationData: any,
  checkPlotBoundaries: any,
  polygonGeoJSON: PolygonGeoJSON,
  newTrialState: NewTrialStateInterface
): void => {
  const cloneGridFeatures: GridFeature[] = [];
  const offsetDistance = 0.25 * (cloneIndex + 1);
  const offsetAngle = 120;

  const replicantGeoJson = replicants.geojson;
  if (
    replicantGeoJson.geometry.coordinates[0] === 0 &&
    replicantGeoJson.geometry.coordinates[1] === 0
  ) {
    gridGeoJSON.features.forEach((feature: any) => {
      const clonedFeature = turf.transformTranslate(
        turf.clone(feature),
        offsetDistance,
        offsetAngle
      );
      cloneGridFeatures.push(clonedFeature);
    });
  } else {
    const clonedProperties = gridGeoJSON.features.map(
      (feature) => feature.properties
    );
    replicantGeoJson.geometry.coordinates.forEach(
      (coordinateSet: any, index: number) => {
        const feature: any = {
          type: "Feature",
          geometry: {
            type: "Polygon",
            coordinates: coordinateSet,
          },
          properties: clonedProperties[index] || {},
        };
        cloneGridFeatures.push(feature);
      }
    );
  }
  let rotationAngle = gridRotations[replicantId] ?? 0;
  let cloneGridGeoJSON: any = turf.featureCollection(cloneGridFeatures);
  const cloneGridSourceId = `cloned-grid-of-trial-plots-${cloneIndex}-${trialPlotId}-${
    replicants.id as number
  }`;
  const cloneGridLayerId = `cloned-grid-layer-${cloneIndex}-${trialPlotId}-${
    replicants.id as number
  }`;

  const transParentLayerId = `cloned-grid-layer-${cloneIndex}-${trialPlotId}`;

  const envelopeCenterMarkerId = `envelope-center-marker-replicant-${
    replicants.id as number
  }`;
  const rotationMarkerId = `rotation-marker-replicant-${
    replicants.id as number
  }`;

  if (markersForTrialPlot[envelopeCenterMarkerId]) {
    markersForTrialPlot[envelopeCenterMarkerId].remove();
  }
  if (markersForTrialPlot[rotationMarkerId]) {
    markersForTrialPlot[rotationMarkerId].remove();
  }

  let cloneEnvelopeCenter = turf.center(cloneGridGeoJSON).geometry.coordinates;
  let rotationMarkerPosition: Array<number> = getNorthWestCorner(
    cloneGridGeoJSON,
    rows,
    columns
  );

  const updateRotationMarkerPosition = (data?: any): void => {
    rotationMarkerPosition = getRotated(
      cloneGridGeoJSON,
      rotationAngle,
      rows,
      columns
    );
    cloneRotationMarker.setLngLat(rotationMarkerPosition as [number, number]);
  };

  const cloneEnvelopeCenterMarker = markerForTrials(
    map,
    cloneEnvelopeCenter as mapboxgl.LngLatLike,
    moveIcon,
    trialPlotId,
    createCustomMarkerElement
  );

  const cloneRotationMarker = markerForTrials(
    map,
    rotationMarkerPosition as [number, number],
    rotateImage,
    trialPlotId,
    createCustomMarkerElement
  );

  markersForTrialPlot[rotationMarkerId] = cloneRotationMarker;
  cloneRotationMarker.on("drag", () => {
    const newCloneCenter = cloneEnvelopeCenterMarker.getLngLat().toArray() as [
      number,
      number
    ];
    const newRotationMarkerPosition = cloneRotationMarker
      .getLngLat()
      .toArray() as [number, number];
    const newAngle = turf.bearing(
      turf.point(newCloneCenter),
      turf.point(newRotationMarkerPosition)
    );
    rotationAngle = newAngle;

    // grid rotations code removed;

    const rotatedGrid = turf.transformRotate(cloneGridGeoJSON, newAngle, {
      pivot: newCloneCenter,
    });
    (map.getSource(cloneGridSourceId) as mapboxgl.GeoJSONSource).setData(
      rotatedGrid
    );
    updateRotationMarkerPosition(rotatedGrid);

    // Update the replicant group label position when rotating
    updateGroupLabelPosition(
      map,
      cloneGridSourceId,
      rotatedGrid,
      newTrialState,
      trialPlotId,
      "'",
      [0, 0],
      12
    );

    trialItem.replicants[cloneIndex].geojson.geometry.coordinates =
      rotatedGrid.features.map((feature: any) => feature.geometry.coordinates);

    // Update each plot label position for replicant during rotation
    updateReplicantPlotLabels(map, rotatedGrid, cloneIndex, trialPlotId);
  });
  markersForTrialPlot[envelopeCenterMarkerId] = cloneEnvelopeCenterMarker;

  cloneRotationMarker.on("dragend", () => {
    // Perform actions after drag is complete, like updating coordinates
    checkPlotBoundaries(
      polygonGeoJSON,
      plotsValidationData,
      setPlotsValidationData,
      newTrialState
    );
    // Report alignment status after user finishes rotating a replicant
    checkAndReportAlignment();
  });
  // Example usage of handleReplicantMove
  cloneEnvelopeCenterMarker.on("drag", () => {
    const newCenter = cloneEnvelopeCenterMarker.getLngLat().toArray() as [
      number,
      number
    ];
    const offset = [
      newCenter[INDEX_ZERO] - cloneEnvelopeCenter[INDEX_ZERO],
      newCenter[INDEX_ONE] - cloneEnvelopeCenter[INDEX_ONE],
    ];

    const newGridFeatures = cloneGridGeoJSON.features.map((feature: any) => {
      const coords = feature.geometry.coordinates[INDEX_ZERO].map(
        (point: [number, number]) => [
          point[INDEX_ZERO] + offset[INDEX_ZERO],
          point[INDEX_ONE] + offset[INDEX_ONE],
        ]
      );
      return turf.polygon([coords], feature.properties);
    });

    cloneGridGeoJSON = turf.featureCollection(newGridFeatures);

    const rotatedGrid = turf.transformRotate(cloneGridGeoJSON, rotationAngle, {
      pivot: newCenter,
    });

    const source = map.getSource(cloneGridSourceId) as mapboxgl.GeoJSONSource;
    if (source) {
      source.setData(rotatedGrid);
    } else {
      map.addSource(cloneGridSourceId, {
        type: "geojson",
        data: rotatedGrid,
      });
    }

    const rotationMarkerPosition = getNorthWestCorner(
      cloneGridGeoJSON,
      rows,
      columns
    );
    cloneRotationMarker.setLngLat(rotationMarkerPosition as [number, number]);
    updateRotationMarkerPosition(rotatedGrid);

    // Update the replicant group label position
    updateGroupLabelPosition(
      map,
      cloneGridSourceId,
      rotatedGrid,
      newTrialState,
      trialPlotId,
      "'",
      [0, 0],
      12
    );

    gridPositions[replicantId] = {
      lng: newCenter[INDEX_ZERO],
      lat: newCenter[INDEX_ONE],
    };

    // Update each plot label position for replicant
    updateReplicantPlotLabels(map, rotatedGrid, cloneIndex, trialPlotId);

    cloneEnvelopeCenter = newCenter;

    trialItem.replicants[cloneIndex].geojson.geometry.coordinates =
      rotatedGrid.features.map((feature: any) => feature.geometry.coordinates);
  });

  cloneEnvelopeCenterMarker.on("dragend", () => {
    // Perform actions after drag is complete, like updating coordinates
    checkPlotBoundaries(
      polygonGeoJSON,
      plotsValidationData,
      setPlotsValidationData,
      newTrialState
    );
    checkAndReportAlignment();
  });

  if (!map.getSource(cloneGridSourceId)) {
    map.addSource(cloneGridSourceId, {
      type: "geojson",
      data: cloneGridGeoJSON,
    });

    map.addLayer({
      id: cloneGridLayerId,
      type: "line",
      source: cloneGridSourceId,
      paint: {
        "line-color": RGBA_TRIAL_PLOT,
        "line-width": 2,
        "line-dasharray": [1, 1], // This makes the line dashed
      },
    });
  } else {
    (map.getSource(cloneGridSourceId) as mapboxgl.GeoJSONSource).setData(
      cloneGridGeoJSON
    );
  }

  trialItem.replicants[cloneIndex].geojson.geometry.coordinates =
    cloneGridGeoJSON.features.map(
      (feature: any) => feature.geometry.coordinates
    );

  setLayOutPropertyOfReplicants(
    cloneGridLayerId,
    replicants.visible as boolean,
    map,
    cloneGridGeoJSON,
    cloneEnvelopeCenter as [number, number],
    cloneGridSourceId,
    cloneEnvelopeCenterMarker,
    cloneRotationMarker,
    trialPlotId
  );

  cloneGridGeoJSON.features.forEach((feature: any, index: any) => {
    // Skip validation for strip plots and their replicants - they should never show red borders
    const isStripPlot = trialItem?.plot?.isStripPlot;
    const isError = isStripPlot
      ? false
      : plotsValidationData?.polygonsNotInsideField?.some(
          (item: any) => item === replicantId
        ) ||
        plotsValidationData.intersectingPairs?.some((item: any) =>
          item.some((plotId: any) => plotId === replicantId)
        );
    const cellLayerId = `cloned-${cloneIndex}-${trialPlotId}-${index}`;
    if (map.getLayer(cellLayerId)) {
      map.removeLayer(cellLayerId);
    }

    map.addLayer({
      id: cellLayerId,
      type: "fill",
      source: cloneGridSourceId,
      paint: {
        "fill-color": ["get", "color"],
        "fill-opacity": 1,
      },
      filter: ["==", ["get", "id"], feature.properties?.id],
    });

    const cellBorderLayerId = `${cellLayerId}-border`;
    if (map.getLayer(cellBorderLayerId)) {
      map.removeLayer(cellBorderLayerId);
    }

    map.addLayer({
      id: cellBorderLayerId,

      type: "line",
      source: cloneGridSourceId,
      paint: {
        "line-color": isError ? RGBA_TRIAL_PLOT_RED : RGBA_TRANSPARENT_COLOR,
        "line-width": 2,
      },
      filter: ["==", ["get", "id"], feature.properties?.id],
    });
  });

  // Add labels for replicant plots
  cloneGridGeoJSON.features.forEach((feature: any, index: any) => {
    const cellLayerId = `cloned-${cloneIndex}-${trialPlotId}-${index}`;
    const labelSourceId = `${cellLayerId}-label`;
    const labelLayerId = `${labelSourceId}-label-plot`;

    // Remove existing label if it exists
    if (map.getLayer(labelLayerId)) {
      map.removeLayer(labelLayerId);
    }
    if (map.getSource(labelSourceId)) {
      map.removeSource(labelSourceId);
    }

    // Add label source
    map.addSource(labelSourceId, {
      type: "geojson",
      data: turf.featureCollection([feature]),
    });

    // Add label layer
    addLabelToMap(
      map,
      labelSourceId,
      `${index + 1}`, // Plot label (1, 2, 3, ...)
      "center",
      [0, 0],
      "plot",
      12
    );
  });

  // Add group label for replicant
  // Find the plotIndex of the parent plot in the trial_plots array
  const parentPlotIndex = newTrialState.trial_plots.findIndex(
    (tp) => tp.plot.id === trialPlotId
  );
  // Use ASCII character for A, B, C... (ensure proper encoding) with prime symbol
  const replicantGroupLabel = String.fromCharCode(65 + parentPlotIndex) + `'`; // A', B', C'...
  const replicantGroupLabelSourceId = `${cloneGridSourceId}-group-label`;
  const replicantGroupLabelLayerId = `${replicantGroupLabelSourceId}-label-group`;

  // Remove existing group label if it exists
  if (map.getLayer(replicantGroupLabelLayerId)) {
    map.removeLayer(replicantGroupLabelLayerId);
  }
  if (map.getSource(replicantGroupLabelSourceId)) {
    map.removeSource(replicantGroupLabelSourceId);
  }

  // Get the bounds of the grid to position the label outside
  const replicantGridBounds = turf.bbox(cloneGridGeoJSON);
  const replicantGridCenter = turf.center(cloneGridGeoJSON);

  // Create a point slightly above the grid for the label
  const replicantLabelPoint = turf.point([
    replicantGridCenter.geometry.coordinates[0],
    replicantGridBounds[3] +
      (replicantGridBounds[3] - replicantGridBounds[1]) * 0.01, // Very small offset above the grid
  ]);

  map.addSource(replicantGroupLabelSourceId, {
    type: "geojson",
    data: replicantLabelPoint,
  });

  // Add group label layer
  addLabelToMap(
    map,
    replicantGroupLabelSourceId,
    replicantGroupLabel, // Group label (A', B', C', ...)
    "bottom", // Anchor at the bottom of the text so it points to the plot
    [0, -0.2], // Small negative y offset to position closer to the plot
    "group",
    14 // Increased size for better visibility
  );
};

const setLayOutPropertyOfReplicants = (
  cloneGridLayerId: string,
  visible: boolean,
  map: mapboxgl.Map,
  cloneGridGeoJSON: GeoJSON.FeatureCollection<GeoJSON.Geometry>,
  cloneEnvelopeCenter: [number, number],
  cloneGridSourceId: string,
  cloneEnvelopeCenterMarker: mapboxgl.Marker,
  cloneRotationMarker: mapboxgl.Marker,
  trialPlotId: number
): void => {
  if (visible) {
    map.setLayoutProperty(cloneGridLayerId, "visibility", "visible");
    cloneEnvelopeCenterMarker.getElement().style.display = "block";
    cloneRotationMarker.getElement().style.display = "block";
    const replicantId = parseInt(cloneGridLayerId.split("-").pop() ?? "0");
    const storedPosition = gridPositions[replicantId];
    const storedRotation = gridRotations[replicantId];
    if (storedPosition && storedRotation !== undefined) {
      const clonedFeatureCollection = turf.featureCollection(
        cloneGridGeoJSON.features.map((feature: any) => {
          const coords = feature.geometry.coordinates[INDEX_ZERO].map(
            (point: [number, number]) => [
              point[INDEX_ZERO] +
                (storedPosition.lng - cloneEnvelopeCenter[INDEX_ZERO]),
              point[INDEX_ONE] +
                (storedPosition.lat - cloneEnvelopeCenter[INDEX_ONE]),
            ]
          );
          return turf.polygon([coords]);
        })
      );
      const rotatedGrid = turf.transformRotate(
        clonedFeatureCollection,
        storedRotation,
        {
          pivot: [storedPosition.lng, storedPosition.lat],
        }
      );
      (map.getSource(cloneGridSourceId) as mapboxgl.GeoJSONSource).setData(
        rotatedGrid
      );

      cloneEnvelopeCenterMarker.setLngLat([
        storedPosition.lng,
        storedPosition.lat,
      ]);
    }
  } else {
    map.setLayoutProperty(cloneGridLayerId, "visibility", "none");
    cloneEnvelopeCenterMarker.getElement().style.display = "none";
    cloneRotationMarker.getElement().style.display = "none";
  }
};

const addRatesAndDosageToMap = (
  ratesDosagesArray: RatesDosageDataInterface[][],
  map: mapboxgl.Map | null,
  trialProducts: any,
  unit_of_rate: string,
  isFertilizerTabActive: boolean,
  activeRatesTab: string,
  bioApplicationIndex: number
): void => {
  if (Array.isArray(ratesDosagesArray)) {
    ratesDosagesArray?.forEach((ratesDosageList, index: number) => {
      ratesDosageList.forEach((ratesDosageItem, itemIndex) => {
        const boxId = `${ratesDosageItem.plot_id as number}`;
        const borderBoxId = `${
          ratesDosageItem.plot_id as number
        }-${index}-border`;
        const plotId = borderBoxId.replace(/-\d+-border$/, "");

        let layerFound = false;
        let fillLayerProcessed = false;

        map?.getStyle()?.layers?.forEach((layer: any) => {
          const filterIdParts = layer?.filter;
          if (filterIdParts && filterIdParts.length > 0) {
            const filterLastIdPart = filterIdParts[filterIdParts.length - 1];
            const isPlotLayer = plotId === filterLastIdPart;

            if (isPlotLayer) {
              layerFound = true;

              // replicant id === plotid, it will have a layer with type fill
              if (layer.type === "fill") {
                fillLayerProcessed = true;
                const rate = ratesDosageItem.rate;
                const variety = ratesDosageItem.variety;
                const fertilizerRate = ratesDosageItem.fertiliser_rate ?? 0;

                const color =
                  activeRatesTab === "biological"
                    ? getCircleColorBiologicals(
                        ratesDosageItem.bioTreatement?.[bioApplicationIndex],
                        ratesDosageItem.bioTreatement?.[bioApplicationIndex]
                          ? ratesDosageItem.bioTreatement?.[bioApplicationIndex]
                              ?.selected_treatment[0]
                          : null
                      )
                    : activeRatesTab === "nutrient"
                    ? getCircleColorNutrient(
                        ratesDosageList,
                        fertilizerRate,
                        undefined,
                        index
                      )
                    : getCircleColor(
                        variety,
                        rate,
                        trialProducts,
                        unit_of_rate,
                        ratesDosageList
                      );

                if (color) {
                  map?.setPaintProperty(layer.id, "fill-color", color);
                }

                map.on("mouseenter", layer.id, (e) => {
                  const myEvent = new CustomEvent("subplotMouseOver", {
                    detail: {
                      message: `focus on from plot ${index} ${itemIndex}`,
                      index,
                      subIndex: itemIndex,
                      type: "enter",
                    },
                  });
                  // Dispatch the custom event
                  window.dispatchEvent(myEvent);
                  map.getCanvas().style.cursor = "pointer";
                });

                map.on("mouseleave", layer.id, () => {
                  const myEvent = new CustomEvent("subplotMouseOver", {
                    detail: {
                      message: `focus removed from plot ${index} ${itemIndex}`,
                      type: "leave",
                      index,
                      subIndex: itemIndex,
                    },
                  });
                  window.dispatchEvent(myEvent);

                  map.getCanvas().style.cursor = "";
                });
              }

              if (layer.type === "line") {
                const collapseValueArray = ratesDosageItem.collapseValue || [];
                const isCollapseIdPresent =
                  collapseValueArray.includes(filterLastIdPart);

                if (plotId === filterLastIdPart) {
                  map?.setPaintProperty(
                    layer.id,
                    "line-color",
                    RGBA_TRIAL_PLOT
                  );
                }
                map?.setPaintProperty(
                  layer.id,
                  "line-width",
                  isCollapseIdPresent ? 4 : 1 // Adjust the width values as needed
                );
              }
            }
          }
        });
      });
    });
  }
};
